/**
 * Integrity watchdog — the app auditing itself (audit 2026-08-01).
 *
 * WHY THIS EXISTS
 * A news-impact stage died and stayed dead for 13 days while the UI rendered its
 * 13-day-old conclusions beside a "scan just now" header. The defect was not in
 * that module: it was that a timestamp existed which nothing ever compared against
 * Date.now(). A subsequent sweep found the same shape in a dozen more places, and
 * an FX path that reported a TRY amount as a USD amount in net worth.
 *
 * The rule this module enforces: every figure the owner could act on must be able
 * to PROVE its freshness, and anything that cannot must say so out loud. An
 * unverifiable input is reported as UNKNOWN, never as healthy — "we have no way to
 * check" and "we checked and it is fine" must never render the same.
 *
 * HARD CONSTRAINTS (this module must not become the next silently-broken thing)
 *  - Writes NO state. Nothing here mutates S, so it can never bloat the cloud blob
 *    or add a save() to a render path.
 *  - Makes NO network and NO LLM calls. It reads timestamps that already exist.
 *  - Has NO imports. Pure derivation from the state object passed in, so it cannot
 *    fail because something upstream failed, and it is trivially testable.
 *  - `now` is injectable so every check is deterministic under test.
 */

/** Severity ordering, so two gradings can be combined without inventing a third rule. */
const RANK = { ok: 0, unknown: 1, warn: 2, critical: 3 };

export const OK = 'ok';
export const WARN = 'warn';
export const CRITICAL = 'critical';
export const UNKNOWN = 'unknown';

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Human-readable age. Mirrors advisory.js _ago() so the app speaks one dialect. */
export function ago(ms) {
  if (ms == null || !isFinite(ms)) return 'unknown';
  if (ms < MIN) return 'just now';
  if (ms < HOUR) return Math.round(ms / MIN) + 'm ago';
  if (ms < DAY) return Math.round(ms / HOUR) + 'h ago';
  return Math.round(ms / DAY) + 'd ago';
}

/** Local, because this module deliberately has no imports. Thousands separator, 0 dp. */
function _n(v) {
  const n = Math.round(Number(v) || 0);
  return String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/^/, n < 0 ? '-' : '');
}

function parseTs(v) {
  if (v == null) return null;
  if (typeof v === 'number') return isFinite(v) && v > 0 ? v : null;
  const t = Date.parse(v);
  return isFinite(t) ? t : null;
}

/**
 * Grade an age against a budget.
 * A missing timestamp is UNKNOWN, never OK — that distinction is the whole point.
 */
function gradeAge(ts, now, warnMs, criticalMs) {
  if (ts == null) return { status: UNKNOWN, ageMs: null };
  const ageMs = now - ts;
  if (criticalMs != null && ageMs > criticalMs) return { status: CRITICAL, ageMs };
  if (ageMs > warnMs) return { status: WARN, ageMs };
  return { status: OK, ageMs };
}

/**
 * Has a tax refund the engine says was PAID been recorded as money in this app?
 *
 * ONE LIST, N CALLERS. The tax page asks this to decide whether to offer the booking
 * button; the check below asks it to decide whether to raise a row. The rule lives here,
 * in the file that already may not import anything, so the page can import it and neither
 * copy can drift from the other.
 *
 * @param {Array}  oneTimeIncome  S.oneTimeIncome
 * @param {Object} refund         { year, amount, paidDate, currency? }
 * @returns {Object|null} the entry that books it, or null
 */
export function taxRefundTagged(oneTimeIncome, year, currency) {
  const cur = currency || 'TRY';
  const y = Number(year);
  const entries = (!isFinite(y) ? [] : (Array.isArray(oneTimeIncome) ? oneTimeIncome : [])
    .filter(e => e && (e.currency || 'TRY') === cur && Number(e.taxYear) === y));
  return { entries, sum: entries.reduce((a, e) => a + (Number(e.amount) || 0), 0) };
}

/** Latest entry date in a set, for a band that has to name one. */
function _latestDate(entries) {
  return entries.reduce((mx, e) => {
    const d = String((e && e.date) || '').slice(0, 10);
    return d && (!mx || d > mx) ? d : mx;
  }, '');
}

export function taxRefundBooking(oneTimeIncome, refund) {
  const list = Array.isArray(oneTimeIncome) ? oneTimeIncome : [];
  const r = refund || {};
  const year = Number(r.year);
  const amt = Number(r.amount);
  const cur = r.currency || 'TRY';
  const day = Date.parse(String(r.paidDate || '').slice(0, 10));
  if (!isFinite(amt)) return null;
  const sameCur = e => e && (e.currency || 'TRY') === cur;
  // 1. The structural link the booking bridge writes — but A TAG IS NOT A RECEIPT. What
  //    clears this check is money, so the tagged rows are SUMMED and have to add up to what
  //    the state paid. Matching on the tag alone meant a mistyped ₺9,366.31 marked a
  //    ₺93,669.31 refund as banked, and a supplementary payment that raised refund_paid kept
  //    the check green over the difference. Summed, because a refund can legitimately arrive
  //    in parts. (audit 2026-08-10)
  const tagged = taxRefundTagged(list, year, cur);
  if (tagged.entries.length) {
    // The SUM and the LATEST date, never the first row: with a refund booked in two parts the
    // band printed the ₺50,000 leg as though it were the whole ₺93,669.31 receipt.
    return Math.abs(tagged.sum - amt) <= 1
      ? { amount: tagged.sum, date: _latestDate(tagged.entries), entries: tagged.entries, via: 'tag' }
      : null;
  }
  // 2. Adoption, for a row entered by hand before the bridge existed. Type, currency and the
  //    paid amount to the lira must agree; the date only has to be within a week, because the
  //    value date on the payment advice and the day the owner notices it in the account are
  //    routinely different. Exact-date matching failed him by one day and then told him to
  //    book the money a SECOND time.
  //
  //    A row already tagged for ANOTHER year is never adopted. Widening the date to a week
  //    made two same-sized refunds five days apart clear each other, and ₺50,000 went missing
  //    from cash with both years reading IN CASH.
  if (!isFinite(day)) return null;
  const found = list.find(e => sameCur(e) && e.type === 'Tax Refund'
    && (e.taxYear == null || Number(e.taxYear) === year)
    && Math.abs(Number(e.amount) - amt) <= 1
    && Math.abs(Date.parse(String(e.date || '').slice(0, 10)) - day) <= 7 * DAY);
  return found
    ? { amount: Number(found.amount), date: String(found.date || '').slice(0, 10), entries: [found], via: 'adopt' }
    : null;
}

/**
 * Full integrity report.
 *
 * @param {Object} S      state object (not mutated)
 * @param {number} [now]  injectable clock
 * @returns {{generatedAt:number, status:string, counts:Object, checks:Array}}
 *   Each check: { id, label, status, detail, consequence, remedy, page, ageMs }
 *   `consequence` states what is silently wrong while the check is failing — a
 *   watchdog that only says "stale" teaches the user to ignore it.
 */
export function integrityReport(S, now = Date.now(), opts = null) {
  const s = S || {};
  const ai = s.aiCopilot || {};
  const checks = [];
  const add = c => checks.push({ ageMs: null, ...c });

  // ── FX: the single highest-blast-radius input ────────────────────────────
  const fxOk = typeof s.fxRate === 'number' && s.fxRate > 0;
  add({
    id: 'fx.rate',
    label: 'USD/TRY rate',
    status: fxOk ? OK : CRITICAL,
    detail: fxOk ? String(s.fxRate) : 'missing',
    consequence: fxOk ? '' : 'TRY and EUR holdings cannot be valued and drop out of net worth.',
    remedy: 'Refresh prices, or enter a manual rate in Settings.',
    page: 'settings',
  });
  {
    const g = gradeAge(parseTs(s._fxTs), now, 6 * HOUR, DAY);
    add({
      id: 'fx.age',
      label: 'FX rate age',
      status: fxOk ? g.status : UNKNOWN,
      ageMs: g.ageMs,
      detail: g.status === UNKNOWN ? 'age unverifiable — no fetch timestamp' : ago(g.ageMs),
      consequence: g.status === OK ? '' : 'Every non-USD figure is converted at a rate of unproven age.',
      remedy: 'Refresh prices.',
      page: 'settings',
    });
  }

  // ── Portfolio mandate: the one field that can silence the advisory ───────
  //
  // `mandateOfPortfolio` falls back to `isDefault ? 'accumulate' : 'active'` when the field is
  // absent, and that fallback ties two independent facts together: which portfolio the app OPENS
  // BY DEFAULT, and which portfolio is buy-and-hold. They coincide today by luck. Make another
  // portfolio the default — one click, a convenience — and the accumulate policy moves with it:
  // the AI stops being allowed to say "take profit" on whatever just inherited it, and starts
  // being allowed to say "exit" on the long-term plan. Nothing anywhere announces either.
  //
  // This app's own rule is that a guess must announce itself. Until 2026-08-13 this one did not.
  {
    const ports = Array.isArray(s.portfolios) ? s.portfolios.filter(p => p && p.status !== 'closed') : [];
    const guessed = ports.filter(p => p.mandate !== 'active' && p.mandate !== 'accumulate');
    add({
      id: 'portfolio.mandate',
      label: 'Portfolio mandate declared',
      status: guessed.length ? WARN : OK,
      detail: guessed.length
        ? `${guessed.length} guessed from isDefault: ${guessed.map(p => p.name || p.id).slice(0, 4).join(', ')}`
        : (ports.length ? `${ports.length} declared` : 'no portfolios'),
      consequence: guessed.length
        ? 'Their mandate is inferred from which portfolio is the default. That inference decides '
          + 'whether advisory may ever recommend selling, trimming or taking profit on the holdings '
          + 'inside them — and it moves silently the day the default changes.'
        : '',
      remedy: 'Portfolio → Edit → Mandate, on each one.',
      page: 'portfolio',
    });
  }

  // ── Portfolio sizing: the second field that decides money, and the one that cannot guess ──
  //
  // `sizing` says HOW a sleeve arrives at its target: weighted (rebalancing bands), accumulate
  // (a standing instruction the app never proposes against) or cyclical (sized by cycle phase,
  // never by drawdown depth). Unlike `mandate` it has NO fallback, deliberately — a guessed
  // 'weighted' puts a sleeve straight back into the deployment plan with nothing on screen
  // saying the value was invented.
  //
  // Two ways a sleeve ends up undeclared, and both need this row. A portfolio created after
  // v55 through some path that does not set it; and the merge case — `mergeArrays` replaces a
  // row wholesale, so a write from a device still on an older build silently drops the field,
  // and v55 can never restore it because `_schemaVersion` is already floored at 55.
  {
    const ports = Array.isArray(s.portfolios) ? s.portfolios.filter(p => p && p.status !== 'closed') : [];
    const undeclared = ports.filter(p => !['weighted', 'accumulate', 'cyclical'].includes(p.sizing));
    add({
      id: 'portfolio.sizing',
      label: 'Portfolio sizing declared',
      status: undeclared.length ? WARN : OK,
      detail: undeclared.length
        ? `${undeclared.length} undeclared: ${undeclared.map(p => p.name || p.id).slice(0, 4).join(', ')}`
        : (ports.length ? `${ports.length} declared` : 'no portfolios'),
      consequence: undeclared.length
        ? 'A sleeve with no sizing is excluded from every deployment plan and from the average-down '
          + 'leg. The bonus page says so, but only while a quarter is pending — everywhere else it is '
          + 'silent. If this appeared without you changing it, a sync from an older build dropped the '
          + 'field and no migration will put it back.'
        : '',
      remedy: 'Portfolio → Edit → Sizing, on each one.',
      page: 'portfolio',
    });
  }

  // ── A cyclical sleeve's last brake ──────────────────────────────────────────────────────
  //
  // On 2026-09-03 the crypto sleeve lost both of its brakes by the owner's decision: the K3
  // price entry gates ($66,000 entry zone, $70,300 channel floor) and the 25-70 unit ceiling.
  // Entry is now governed by the halving clock alone. That is a legitimate call and it leaves
  // exactly ONE mechanical stop standing: the invalidation levels, BTC $52,500 / ETH $1,384 /
  // XRP $0.87.
  //
  // A stop that cannot be evaluated is not a stop. If a price refresh has not landed, the
  // sleeve has no brake of any kind and nothing else in the app would say so. This row exists
  // to say so.
  {
    const gatesFn = (opts && typeof opts.cycleGates === 'function') ? opts.cycleGates : null;
    const levels = (opts && opts.cycleInvalidations && typeof opts.cycleInvalidations === 'object')
      ? opts.cycleInvalidations : null;
    const ports = Array.isArray(s.portfolios) ? s.portfolios.filter(p => p && p.status !== 'closed') : [];
    const cyc = ports.filter(p => p.sizing === 'cyclical');
    const prices = (s && s.prices) || {};
    let res = null, stale = [];
    if (gatesFn && levels) {
      const closes = {};
      Object.keys(levels).forEach(k => {
        const q = prices[k];
        if (q && q.price > 0) closes[k] = q.price;
        // The plan specifies a WEEKLY close and this is the last spot mark. Said, not hidden.
        if (q && q.ts && (now - q.ts) > 7 * 24 * 3600 * 1000) stale.push(k);
      });
      res = gatesFn(closes);
    }
    const unpriced = res ? (res.unpriced || []) : [];
    const hit = res ? (res.invalidated || []) : [];
    add({
      id: 'cycle.brakes',
      label: 'The cyclical sleeve still has a stop',
      status: !cyc.length ? OK
        : (!gatesFn || !levels) ? UNKNOWN
          : hit.length ? CRITICAL
            : unpriced.length ? WARN : OK,
      detail: !cyc.length ? 'no cyclical sleeves'
        : (!gatesFn || !levels) ? 'cycle gate evaluator not injected by this caller'
          : hit.length ? `INVALIDATED: ${hit.map(x => `${x.ticker} ${x.close} below ${x.level}`).join(' · ')}`
            : unpriced.length ? `${unpriced.join(', ')} unpriced — their stop cannot fire`
              : `${Object.keys(levels).length} stop(s) priced and live${stale.length ? ` · ${stale.join(', ')} mark is over a week old` : ''}`,
      consequence: !cyc.length ? ''
        : hit.length
          ? 'The plan calls this thesis broken below these levels. Every cycle leg is stopped until you decide otherwise.'
          : unpriced.length
            ? 'This sleeve has no ceiling and no price entry gate by your own decision, so the invalidation level is the only brake left. Unpriced, there is no brake at all and no other screen will tell you.'
            : '',
      remedy: 'Settings → refresh prices, or enter BTC/ETH/XRP manually.',
      page: 'settings',
    });
  }

  // ── An accumulate sleeve's price trigger: the thing that replaced its exit licence ──────
  //
  // Setting `sizing: 'accumulate'` and `mandate: 'accumulate'` together means the app will never
  // propose acting on that sleeve. On SAP XETRA that is the owner's explicit instruction, and the
  // ATH trigger is what compensates: it ALERTS, it never emits a sell directive. A trigger that
  // is missing, or whose currency cannot be matched to the price the app actually holds, means
  // the compensation is not there and the silence is total.
  {
    const ports = Array.isArray(s.portfolios) ? s.portfolios.filter(p => p && p.status !== 'closed') : [];
    const acc = ports.filter(p => p.sizing === 'accumulate');
    // This module imports NOTHING by contract, so the currency-matched comparison is INJECTED
    // rather than re-implemented here. Both `integrity-page.js` and `dashboard.js` inject it;
    // without it the row reports UNKNOWN, which is the honest answer and not an OK.
    const evalTrigger = (opts && typeof opts.evaluateTrigger === 'function') ? opts.evaluateTrigger : null;
    const problems = [], hits = [], near = [];
    if (evalTrigger) acc.forEach(p => {
      const t = p.trigger;
      if (!t || t.type !== 'price_level') { problems.push(`${p.name || p.id}: no trigger`); return; }
      const q = (s.prices || {})[t.ticker];
      if (!q || !(q.price > 0)) { problems.push(`${p.name || p.id}: no price for ${t.ticker}`); return; }
      const cur = q.cur || q.currency || null;
      const r = evalTrigger(t, q.price, cur);
      if (!r || r.ok !== true) {
        problems.push(`${p.name || p.id}: ${(r && r.why) || 'trigger unreadable'}`);
        return;
      }
      if (r.hit) hits.push(`${p.name || p.id}: ${t.ticker} at ${r.price} ${r.currency}, ${r.direction} ${r.level}`);
      else near.push(`${p.name || p.id}: ${r.distancePct}% from ${r.level} ${r.currency}`);
    });
    add({
      id: 'portfolio.accumulateTrigger',
      label: 'Accumulate sleeves carry a usable alert',
      // A HIT outranks a configuration problem: it is the one moment this row exists for.
      status: !acc.length ? OK
        : !evalTrigger ? UNKNOWN
          : (hits.length ? CRITICAL : (problems.length ? WARN : OK)),
      detail: !acc.length ? 'no accumulate sleeves'
        : !evalTrigger ? 'trigger evaluator not injected by this caller'
          : hits.length ? hits.slice(0, 3).join(' · ')
          : (problems.length ? problems.slice(0, 3).join(' · ') : near.slice(0, 3).join(' · ') || `${acc.length} armed`),
      consequence: hits.length
        ? 'The level you set has been reached. This sleeve is deliberately excluded from every plan '
          + 'and its advisory may not propose an exit, so this row is the only thing that will ever say so.'
        : problems.length
          ? 'This sleeve is excluded from every plan AND its advisory may not suggest an exit. The '
            + 'price alert is the only thing left that would ever tell you to look at it, and it cannot fire.'
          : '',
      remedy: 'Portfolio → Edit → set a price level in the sleeve\'s own currency.',
      page: 'portfolio',
    });
  }

  // ── Import allowlists vs the rows that actually exist ────────────────────
  //
  // `_IMPORT_KEYS` is a DELETION list: on import every row in these arrays is reduced to the
  // keys named there. It is hand-written, and on 2026-08-13 a commit whose entire purpose was
  // to stop hand-written lists added one from guesswork. It omitted `oneTimeIncome[].type`,
  // the next import stripped it from both of the owner's rows, and `taxRefundBooking` — which
  // adopts a refund by `e.type === 'Tax Refund'` — reported ₺93,669.31 as not in cash on money
  // that had never moved.
  //
  // A list nobody can keep in sync will fall out of sync. So stop asking anyone to: compare it
  // against the state in front of us. Every key on a live row that the allowlist would delete
  // is reported here, by array and by name, BEFORE the next import deletes it.
  // ── Two records cannot share a name ──────────────────────────────────────
  // DATA-013, owed since `abc611d`. `uid()` drew from 36⁵ and collided 0.0065% of the time per
  // 100 ids; it is collision-proof within a process now, but nothing GRADED the property, and
  // the 789-of-790 count that closed it was made by hand at a console.
  //
  // WHY IT IS GLOBAL AND NOT PER-ARRAY. `mergeState` builds ONE flat `mergedDel` set from both
  // sides' `_deletedIds` and passes that same set to `mergeArrays` for every key in
  // MERGE_ARRAYS. A tombstone is therefore an id, not an (array, id) pair — deleting a cash row
  // removes any holding, trade or dividend that happens to share its id. `softDelete` and every
  // `findIndex(x => x.id === id)` inherit the same assumption.
  //
  // Nested collections count: `holdings[].lots` and `liabilities[].payments` are what `dv.lotId`
  // and `_paymentId` point at, so a collision there breaks v50's and v51's back-links.
  //
  // MERGE_ARRAYS is INJECTED, not restated — the rule that closed CURRENT_SCHEMA, RESTORE_KEYS,
  // the deploy gate and INTERNAL_TYPES. Without it the check reports UNKNOWN rather than
  // grading a list it made up. Live 2026-08-14: 678 ids, 678 distinct, 0 duplicates.
  {
    const mergeArrays = (opts && Array.isArray(opts.mergeArrays)) ? opts.mergeArrays : null;
    const idCommon = {
      label: 'Two records sharing an id',
      consequence: 'A tombstone is a bare id, applied to every merge-synced array at once — so two records with one id die together on the next sync, `findIndex` silently returns whichever comes first, and the `lotId` / `_paymentId` back-links resolve to the wrong record.',
      remedy: 'Money → Cash Transactions and Portfolio: find the pair named below and re-enter one of them, which mints a fresh id.',
      page: 'settings',
    };
    if (!mergeArrays) {
      add({ ...idCommon, id: 'data.idsDistinct', status: UNKNOWN, detail: 'the merge-array list was not supplied this session' });
    } else {
      const seen = new Map();
      const dups = [];
      const put = (id, where) => {
        if (typeof id !== 'string' || !id) return;
        if (seen.has(id)) dups.push(`${id} in ${seen.get(id)} and ${where}`);
        else seen.set(id, where);
      };
      mergeArrays.forEach(k => {
        const rows = Array.isArray(s[k]) ? s[k] : null;
        if (!rows) return;
        rows.forEach(r => {
          if (!r || typeof r !== 'object') return;
          put(r.id, k);
          if (Array.isArray(r.lots)) r.lots.forEach(l => l && put(l.id, `${k}[].lots`));
          if (Array.isArray(r.payments)) r.payments.forEach(p => p && put(p.id, `${k}[].payments`));
        });
      });
      if (dups.length) {
        add({ ...idCommon, id: 'data.idsDistinct', status: CRITICAL,
          detail: `${dups.length} shared id(s): ${dups.slice(0, 3).join(' · ')}${dups.length > 3 ? ` · +${dups.length - 3} more` : ''}` });
      } else {
        add({ ...idCommon, id: 'data.idsDistinct', status: OK,
          detail: `${seen.size} id(s) across ${mergeArrays.length} synced array(s), all distinct` });
      }
    }
  }

  {
    const importKeys = (opts && opts.importKeys) || null;
    if (!importKeys) {
      add({
        id: 'import.keyLoss',
        label: 'Import would preserve every field',
        status: UNKNOWN,
        detail: 'allowlists not supplied this session',
        consequence: 'An import can silently delete a field the app depends on, and nothing would say so.',
        remedy: 'Reload the page.',
        page: 'settings',
      });
    } else {
      const losses = [];
      Object.keys(importKeys).forEach(arr => {
        const rows = Array.isArray(s[arr]) ? s[arr] : null;
        if (!rows || !rows.length) return;
        const allow = importKeys[arr] || [];
        const seen = new Set();
        rows.forEach(r => { if (r && typeof r === 'object') Object.keys(r).forEach(k => seen.add(k)); });
        const stripped = [...seen].filter(k => !allow.includes(k));
        if (stripped.length) losses.push(arr + '.' + stripped.join('/'));
      });
      add({
        id: 'import.keyLoss',
        label: 'Import would preserve every field',
        status: losses.length ? WARN : OK,
        detail: losses.length ? losses.slice(0, 5).join(' · ') : 'every live field is on its allowlist',
        consequence: losses.length
          ? 'These fields exist on rows in this state and are NOT on the import allowlist, so the '
            + 'next backup restore deletes them — silently, and reporting success.'
          : '',
        remedy: 'Add them to _IMPORT_KEYS in core/state.js, or confirm they are meant to be dropped.',
        page: 'settings',
      });
    }
  }

  // ── Prices ───────────────────────────────────────────────────────────────
  const holdings = Array.isArray(s.holdings) ? s.holdings : [];
  const prices = s.prices || {};
  const priced = holdings.filter(h => h && (h.qty || 0) > 0 && h.type !== 'Cash');
  const missing = priced.filter(h => !prices[h.ticker]).map(h => h.ticker);
  const stale = priced.filter(h => {
    const p = prices[h.ticker];
    const t = p && parseTs(p.ts);
    return p && (t == null || now - t > DAY);
  }).map(h => h.ticker);
  add({
    id: 'prices.missing',
    label: 'Holdings without a price',
    status: missing.length ? WARN : OK,
    detail: missing.length ? `${missing.length}: ${missing.slice(0, 6).join(', ')}` : 'none',
    consequence: missing.length ? 'These are counted at cost, not market value.' : '',
    remedy: 'Refresh prices, or enter them manually in Settings.',
    page: 'settings',
  });
  add({
    id: 'prices.stale',
    label: 'Prices older than 24h',
    status: stale.length ? WARN : OK,
    detail: stale.length ? `${stale.length}: ${stale.slice(0, 6).join(', ')}` : 'none',
    consequence: stale.length ? 'Their market value, P&L and weights are yesterday\'s.' : '',
    remedy: 'Refresh prices.',
    page: 'settings',
  });

  // ── Is the tax engine still running? ─────────────────────────────────────
  //
  // `taxengine.py` runs unattended — launchd, on every document drop and nightly at 21:00 — and
  // it fails **loudly to its own log and silently to everything else**: `autorun.sh` exits when
  // `pdftotext` is missing (the log shows this has actually happened), and `results.json` simply
  // stays as it was. The Tax page marks a snapshot STALE past 35 days; THIS page, the one he
  // opens to ask "is anything wrong", carried no tax check but `tax.refundBooked`.
  //
  // Thresholds: the agent rewrites `generated` on every nightly run, so a healthy pipeline is
  // never more than a day old. 7 days is already abnormal without firing on a week away from the
  // machine; 35 matches the number the Tax page already uses for its own badge, so the two
  // surfaces cannot disagree about what stale means.
  {
    const seen = (opts && Array.isArray(opts.taxSnapshots)) ? opts.taxSnapshots : null;
    // NULL IS NOT EMPTY, and EMPTY IS NOT STALE. `null` = nobody has read the snapshot store yet;
    // `[]` = read, and there are none — an install that does not use the tax module must not
    // carry a permanent warning it can never clear. (The same trap `publishTaxRefunds` fell into
    // on 2026-08-10, which cost a slot in the action bell.)
    const newest = seen && seen.length
      ? seen.reduce((max, snap) => {
        const t = parseTs(snap && snap.generated);
        return (t != null && (max == null || t > max)) ? t : max;
      }, null)
      : null;
    const g = seen === null ? { status: UNKNOWN, ageMs: null }
      : !seen.length ? { status: OK, ageMs: null }
        : gradeAge(newest, now, 7 * DAY, 35 * DAY);
    add({
      id: 'tax.snapshotAge',
      label: 'Tax engine last run',
      status: g.status,
      ageMs: g.ageMs,
      detail: seen === null ? 'snapshots not read yet'
        : !seen.length ? 'no tax snapshots on this device'
          : newest == null ? 'snapshot carries no run date'
            : ago(g.ageMs) + ' (' + seen.map(x => x.year).join(', ') + ')',
      consequence: (g.status === OK || g.status === UNKNOWN) ? ''
        : 'Every figure on the Tax page — refund, deductions, receipt windows — is from the last successful run, and any document dropped since then has not been counted.',
      remedy: 'Check taxes/engine/autorun.log. The usual cause is a missing pdftotext (brew install poppler) or an unloaded launchd agent.',
      page: 'tax',
    });
  }

  // ── Does the Monthly row say what the bank said? ─────────────────────────────────────────
  //
  // A card row in Monthly is one typed number; the statement engine reads the bank's own
  // PDF. The first live case (2026-10-03): October's Bonus row was a copy of September's
  // (₺115,269) while the October statement said ₺119,814.39. Nothing in the app could see the
  // gap, because the statements lived on disk. Now the newest statement per card crosses
  // through `statement-bridge.js` as four facts and is held against the row of its DUE month.
  //
  // Tolerance ₺1: the row is typed rounded. A statement the engine could not reconcile to the
  // bank's declared total (`needs_review`) is itself a warning - its number is not trusted yet.
  // Same NULL IS NOT EMPTY rule as the tax checks: `null` = snapshots not read, `[]` = none.
  {
    const facts = (opts && Array.isArray(opts.statements)) ? opts.statements : null;
    const CARD_NAME = { axess: 'Axess', bonus: 'Bonus', pgs: 'Pgs', maximum: 'Maximum' };
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = ym => (/^\d{4}-\d{2}$/.test(String(ym || ''))) ? MON[Number(ym.slice(5, 7)) - 1] + ' ' + ym.slice(0, 4) : String(ym || '?');
    const fT = n => '₺' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
    const common = {
      id: 'cashflow.statementMismatch',
      label: 'Monthly card rows vs bank statements',
      page: 'cashflow',
      remedy: 'Open Monthly for the due month and type the statement total on the card row; the engine\'s STATUS.md carries the figure.',
    };
    if (facts === null) {
      add({ ...common, status: UNKNOWN, detail: 'statements not read yet', consequence: '' });
    } else if (!facts.length) {
      add({ ...common, status: OK, detail: 'no statements on this device', consequence: '' });
    } else {
      const bad = [], okLines = [], unknowns = [];
      facts.forEach(f => {
        const name = CARD_NAME[f.card] || String(f.card);
        const ym = f.due_month;
        const mo = s.cashflow && s.cashflow[ym];
        if (f.status && f.status !== 'ok') { bad.push(name + ' ' + monthLabel(ym) + ': statement needs review (engine could not reconcile it)'); return; }
        // a total the parser did not read is its own finding, never a comparison against ₺0
        if (!Number.isFinite(Number(f.total_due))) { bad.push(name + ' ' + monthLabel(ym) + ': statement total not read'); return; }
        if (!mo) { unknowns.push(name + ' ' + monthLabel(ym) + ': no Monthly month yet'); return; }
        const row = (mo.items || []).find(it => it && String(it.name || '').trim().toLowerCase() === name.toLowerCase());
        if (!row) { bad.push(name + ' ' + monthLabel(ym) + ': no card row in Monthly'); return; }
        // a row that exists but was never typed (the live book's Maximum row on 2026-10-03) is "not typed", not ₺0
        if (row.amount == null || row.amount === '') { bad.push(name + ' ' + monthLabel(ym) + ': card row not typed yet (statement ' + fT(f.total_due) + ')'); return; }
        const diff = Number(row.amount) - Number(f.total_due);
        if (Math.abs(diff) <= 1) okLines.push(name + ' ' + monthLabel(ym));
        else bad.push(name + ' ' + monthLabel(ym) + ': row ' + fT(row.amount) + ' vs statement ' + fT(f.total_due) + ' (' + (diff > 0 ? '+' : '') + fT(diff).replace('₺-', '-₺') + ')');
      });
      const status = bad.length ? WARN : OK;
      add({
        ...common,
        status,
        detail: bad.length ? bad.join(' · ') : (okLines.length + ' of ' + facts.length + ' card rows match the bank' + (unknowns.length ? ' · ' + unknowns.join(' · ') : '')),
        consequence: bad.length ? 'The cash plan, the surplus and the forward basis carry the typed figure, not what the bank will take on the due date.' : '',
      });
    }
  }

  // ── Reserve fund legs the app cannot value ───────────────────────────────
  //
  // `prices.missing` above covers HOLDINGS. It has never covered the reserve fund, and the
  // reserve fund is where the metals live — 15.09 g XAU and 111.27 g XAG on the live book, about
  // ₺115,000. `rfToTRY` returns null for a leg it cannot price, every aggregate coerces that to
  // 0 inside a `+=`, and net worth quietly shrinks by the amount. No page said so.
  //
  // ⚠ THIS DUPLICATES THE RULE IN `cash.rfToTRY`, KNOWINGLY. This module may not import — see the
  // header: it must not be able to fail because something upstream failed. Two copies of a rule
  // is the failure `secrets.js` opens with, so the copies are pinned against each other by a
  // matrix test in `a-reserve-leg-must-not-invent-its-own-rate`, rather than by care.
  {
    const rf = (s.reserveFund && typeof s.reserveFund === 'object') ? s.reserveFund : {};
    const metalPriced = c => { const p = prices[c]; return !!p && Number(p.price) > 0; };
    const canValue = c => {
      switch (c) {
        case 'TRY': return true;
        case 'USD': return Number(s.fxRate) > 0;
        case 'EUR': return Number(s.fxRateEUR) > 0;
        case 'GBP': return Number(s.fxRate) > 0 && Number(s.fxGBPUSD) > 0;
        case 'XAU': case 'XAG': return metalPriced(c);
        default: return false;
      }
    };
    const held = Object.keys(rf).filter(c => Number(rf[c]) > 0);
    const unpriced = held.filter(c => !canValue(c));
    add({
      id: 'reserve.unpriced',
      label: 'Reserve fund legs that can be valued',
      status: unpriced.length ? CRITICAL : OK,
      detail: unpriced.length
        ? `${unpriced.length} of ${held.length} cannot: ${unpriced.join(', ')}`
        : (held.length ? `all ${held.length}` : 'no reserves held'),
      consequence: unpriced.length
        ? 'These legs count as ZERO in the reserve total, in reserve coverage and in net worth. A deposit or withdrawal against app cash is refused while this stands.'
        : '',
      remedy: 'Refresh prices, or enter the missing rate or metal price in Settings.',
      page: 'settings',
    });
  }

  // ── Open trades with no price: valued at entry cost, P&L reported as zero,
  //    and unlike holdings there is no "awaiting price" badge for them. ──────
  const openTrades = (Array.isArray(s.trades) ? s.trades : [])
    .filter(t => t && t.status !== 'Closed' && t.type !== 'TimeDeposit');
  const unpricedTrades = openTrades.filter(t => !prices[t.ticker]).map(t => t.ticker);
  add({
    id: 'trades.unpriced',
    label: 'Open trades without a price',
    status: unpricedTrades.length ? CRITICAL : OK,
    detail: unpricedTrades.length ? unpricedTrades.slice(0, 6).join(', ') : 'none',
    consequence: unpricedTrades.length
      ? 'They sit in net worth at entry cost showing exactly $0 P&L, which reads as a flat position.'
      : '',
    remedy: 'Refresh prices, or enter them manually in Settings.',
    page: 'settings',
  });

  // ── A STOP THAT WAS BREACHED WHILE NOTHING WAS WATCHING STAYS BREACHED ────
  //
  // The alert for this exists and works. Measured on the live book 2026-10-02: every one of the
  // five open BIST trades fired its SL notification -- ZOREN 08-31, CEMTS 09-21, ALCAR 09-28,
  // ARCLK and PGSUS 09-30. Nothing was broken. What does not exist anywhere is the STANDING
  // fact, and three things compound to erase it:
  //
  //   1. `scanTradeCrossings` fires on a CROSSING: `px <= level && (prev == null || prev > level)`.
  //      Once price sits below the stop, `prev` is below it too and the condition is never true
  //      again. The alert is an event, by design, and the design comment says so.
  //   2. `alertedKeys` is pruned at 30 days (state.js:1155). ZOREN's key is already gone, its
  //      price has been under its stop continuously since 08-31, and so it can neither re-fire
  //      nor show that it fired. The evidence and the trigger expire together.
  //   3. The proximity rail clamps the breach to zero -- `Math.max(0, ...)` in trades.js -- and
  //      shows the top five by descending proximity, so the deepest breaches sort LAST and fall
  //      behind "show all". A position 20% under its stop renders identically to one exactly at it.
  //
  // So the book was right, the alarm was right, and the screen still said nothing. This check is
  // the standing half: it is derived from price every time it runs and cannot expire.
  //
  // SCOPE IS THE SCANNER'S OWN: only an EXPLICIT `t.sl`. `effectiveStop` would also report the
  // leverage-aware default for the trades that never declared one, but `scanTradeCrossings` does
  // not alert on that level (`check(t.sl, 'SL')`), and a watchdog that reports a level no alarm
  // watches is a different feature wearing this one's clothes. `trades.noStop` can be that
  // feature if it is ever wanted; it is not this.
  //
  // WARN, not CRITICAL: nothing here is a wrong number. It is the same shape as
  // `positions.cutNotExited` -- the policy says closed and the book says open -- and that one is
  // WARN for the same reason.
  {
    const breached = openTrades
      .filter(t => typeof t.sl === 'number' && isFinite(t.sl) && t.sl > 0)
      .map(t => {
        const px = prices[t.ticker] && prices[t.ticker].price;
        if (typeof px !== 'number' || !isFinite(px) || px <= 0) return null;
        // A short is stopped out ABOVE its stop. Reading both sides off one comparison is what
        // made the SL branch in the scanner two lines rather than one.
        const past = (t.side === 'Short') ? (px >= t.sl) : (px <= t.sl);
        if (!past) return null;
        const depthPct = (t.side === 'Short') ? (px - t.sl) / t.sl * 100 : (t.sl - px) / t.sl * 100;
        return { ticker: t.ticker, px, sl: t.sl, side: t.side || 'Long', depthPct };
      })
      .filter(Boolean)
      .sort((a, b) => b.depthPct - a.depthPct);
    const common = {
      id: 'trades.beyondStop',
      label: 'Open trades past their stop',
      consequence: 'The stop is the exit the position declared for itself and it has been passed. The SL alert fires on the crossing only, so a breach that happened while the app was closed leaves no standing signal anywhere, and the proximity rail clamps it to zero and sorts it out of view.',
      remedy: 'Trades → exit at the actual fill, or move the stop deliberately. Leaving it is a decision too; this row is the record that it has not been made.',
      page: 'trades',
    };
    if (breached.length) {
      add({ ...common, status: WARN,
        detail: `${breached.length} position(s) past their stop: ${breached.slice(0, 4).map(b => `${b.ticker} ${b.px} vs SL ${b.sl} (${b.depthPct.toFixed(1)}% beyond)`).join(', ')}${breached.length > 4 ? ` +${breached.length - 4} more` : ''}` });
    } else {
      add({ ...common, status: OK, detail: 'every declared stop is still unbreached' });
    }
  }

  // ── AI pipeline ──────────────────────────────────────────────────────────
  {
    const g = gradeAge(parseTs(ai.lastScan && ai.lastScan.ts), now, 12 * HOUR, 2 * DAY);
    add({
      id: 'ai.scan',
      label: 'AI scan',
      status: g.status,
      ageMs: g.ageMs,
      detail: g.status === UNKNOWN ? 'never run' : ago(g.ageMs),
      consequence: g.status === OK ? '' : 'Recommendations reflect an older portfolio and market.',
      remedy: 'Open the AI page and run a scan.',
      page: 'advisory',
    });
  }
  {
    const li = ai.lastImpact || null;
    const g = gradeAge(parseTs(li && li.ts), now, 6 * HOUR, DAY);
    const failing = !!(li && li.error);
    add({
      id: 'ai.impact',
      label: 'News impact stage',
      status: failing ? CRITICAL : g.status,
      ageMs: g.ageMs,
      detail: failing ? String(li.error) : (g.status === UNKNOWN ? 'never run' : ago(g.ageMs)),
      consequence: (failing || g.status !== OK)
        ? 'Impact-driven recommendation rules are skipped, and the evidence shown is the last successful run.'
        : '',
      remedy: 'Open the AI page and run a scan; check the console for [IMPACT].',
      page: 'advisory',
    });
  }
  {
    // Written by cacheTAData. Without it, scenario alerts (RSI, support) can fire
    // forever off a snapshot whose age nobody can establish.
    const g = gradeAge(parseTs(ai._taCacheTs), now, DAY, 3 * DAY);
    add({
      id: 'ai.ta',
      label: 'Technical-analysis cache',
      status: g.status,
      ageMs: g.ageMs,
      detail: g.status === UNKNOWN ? 'age unverifiable — no timestamp' : ago(g.ageMs),
      consequence: g.status === OK ? '' : 'RSI and support levels driving scenario alerts may be stale.',
      remedy: 'Run an AI scan.',
      page: 'advisory',
    });
  }
  {
    // Consumed by the Bonus planner to choose a deployment profile, i.e. real money.
    const g = gradeAge(parseTs(ai._fgCacheTs), now, DAY, 3 * DAY);
    add({
      id: 'ai.fg',
      label: 'Fear & Greed cache',
      status: g.status,
      ageMs: g.ageMs,
      detail: g.status === UNKNOWN ? 'age unverifiable — no timestamp' : ago(g.ageMs),
      consequence: g.status === OK ? '' : 'The Bonus deployment planner reads this to pick an appetite.',
      remedy: 'Run an AI scan.',
      page: 'advisory',
    });
  }

  {
    // The card deck on the AI page. It is PERSISTED (S.aiInsights.items) and rendered with no
    // visible age, so a stale deck is indistinguishable from a fresh one. On 2026-08-08 the
    // deck on screen was generated 2026-06-05 — 64 days — and was still telling the owner he
    // had "10 open trades with no stop-loss" (two had closed, six carry a stop) and quoting
    // Turkish inflation at ~42% against an actual 33.5%. Every other AI surface here already
    // had an age check; this one, the only one that renders prose the owner reads as current,
    // did not. (audit 2026-08-08)
    const ins = s.aiInsights || {};
    const count = Array.isArray(ins.items) ? ins.items.length : 0;
    const g = gradeAge(parseTs(ins.generatedAt), now, 3 * DAY, 7 * DAY);
    add({
      id: 'ai.insights',
      label: 'AI insight cards',
      status: count === 0 ? OK : g.status,
      ageMs: g.ageMs,
      detail: count === 0
        ? 'no cards'
        : (g.status === UNKNOWN ? 'age unverifiable — no generatedAt' : `${count} card(s), ${ago(g.ageMs)}`),
      consequence: (count > 0 && g.status !== OK)
        ? 'The cards state holdings, trade counts and rates as current fact; at this age they describe a portfolio that has since changed.'
        : '',
      remedy: 'Open the AI page and regenerate insights.',
      page: 'advisory',
    });
  }

  // ── Cash: the app's TRY figure against what the banks say ─────────────────
  //
  // `S.cash.TRY` is maintained, not verified. On 2026-08-08 it read ₺234,384.75 against
  // ₺336,800 actually held, and no surface in the app could have shown the ₺102,415 gap,
  // because until `cashRecon` existed there was no second number to disagree with it. Both
  // checks below exist so that a figure the owner spends against cannot drift in silence.
  {
    const rec = (s.cashRecon && s.cashRecon.TRY) || null;
    const banks = (rec && rec.banks && typeof rec.banks === 'object') ? rec.banks : null;
    const names = banks ? Object.keys(banks) : [];
    if (!names.length) {
      add({
        id: 'cash.bankSplit',
        label: 'TRY cash vs banks',
        status: UNKNOWN,
        detail: 'never reconciled — no bank balances on record',
        consequence: 'Nothing establishes that the TRY figure is a real balance.',
        remedy: 'Money → Cash Transactions → Reconcile TRY.',
        page: 'cash',
      });
    } else {
      // NOT a live comparison. Trades, FX conversions and portfolio buys move S.cash.TRY by
      // design, so `sum(banks) === S.cash.TRY` holds for exactly as long as it takes to open
      // one position — it went critical on a ₺20,000 purchase in testing, with nothing wrong.
      // What is worth grading is the reading itself: does the breakdown add up to the total
      // that was recorded, and how far off was the app when it was last checked. A large
      // correction means something is leaving the account that the app never sees.
      const sum = names.reduce((a, k) => a + (Number(banks[k]) || 0), 0);
      const total = Number(rec.total);
      const recorded = isFinite(total) ? total : null;
      const inconsistent = recorded != null && Math.abs(sum - recorded) > 0.01;
      const drift = Number(rec.lastDrift);
      const known = isFinite(drift);
      // 2% of the reading, floored at ₺2,000 — below that it is rounding and interest, not a leak.
      const tol = Math.max(2000, Math.abs(sum) * 0.02);

      // ── MOST OF THE CORRECTION IS SOMETHING THE APP ALREADY KNOWS ─────────────────────────
      //
      // Grading the raw drift made this row warn on every payday and every mid-month check, and
      // say the one thing that was not true: "something is moving money it never sees." Two
      // movements are DELIBERATELY not credited to `S.cash.TRY`, both by design, and the
      // reconciliation is where they land:
      //
      //   · a salary row is an `_audit` mirror (`capital.js:293` → TX_CLASS.MIRROR). The bank goes
      //     up, the cash ledger does not, so the next reading finds the whole salary as a gap.
      //   · an income-funded expense moves nothing either. `cashflow.js:1229` debits `S.cash.TRY`
      //     only for `paidBy === 'savings'` with `paidBySource === 'cash'`; everything paid out of
      //     the month's income leaves the bank and no ledger row.
      //
      // Measured on the live book 2026-10-02, window by window between consecutive readings:
      //
      //     window end       drift      salary   income-funded    unexplained
      //     2026-09-01    +194,943    +234,094        -39,151              0
      //     2026-09-11    -112,431           0       -189,938        +77,507
      //     2026-09-21    -112,038           0       -112,144           +106
      //     2026-10-02    +226,927    +231,391        -13,905         +9,441
      //
      // Two windows land on the number almost exactly, which is what says the formula is the right
      // one rather than a tolerance widened until the warning stopped. ₺9,441 is worth a look;
      // ₺226,927 was worth nothing but being ignored.
      //
      // The window is read from the ledger, not from `cashRecon`, which stores only the latest
      // reading: `hi` is `rec.at`, `lo` is the most recent reconciliation row before it. A reading
      // that found zero drift writes no row (cash.js only appends when `delta !== 0`), which is
      // harmless here — a zero drift has nothing to explain.
      //
      // ⚠ `confirmedAt` IS A STAMP TIME, NOT ALWAYS A PAYMENT TIME. `stampConfirmedBeforeReading`
      // back-stamps a whole backlog at the moment of a reading, and on 2026-08-25 that put
      // ₺1,376,071 of already-paid items inside one window and "explained" a -₺128,199 drift as
      // -₺1,376,071. An item belonging to a month that closed before the window opened is a
      // back-stamp, not a payment, and is excluded and counted separately. With that guard the
      // same window reads ₺99,708 unexplained — still over tolerance, and correctly so: it
      // straddles the reading this feature was built from and is not explainable.
      const _recDates = (Array.isArray(s.cashTxs) ? s.cashTxs : [])
        .filter(t => t && String(t.note || '').indexOf('Cash reconciliation to bank balances') === 0)
        .map(t => String(t.date || '')).filter(Boolean).sort();
      const _hi = String(rec.at || '') || (_recDates.length ? _recDates[_recDates.length - 1] : '');
      // ⚠ `rec.at` IS ONE MILLISECOND AFTER THE ROW THE SAME READING WROTE. `reconcileTRY` appends
      // the cashTx and then takes `now()` for `cashRecon.at`, so a naive "latest row before
      // `rec.at`" picks the reading's OWN row and collapses the window to 1 ms, explaining exactly
      // nothing. Find the reading's own row first, then step back one. No epsilon, no assumption
      // about how far apart the two timestamps land.
      let _ownIdx = -1;
      _recDates.forEach((dt, k) => { if (dt <= _hi) _ownIdx = k; });
      const _lo = _ownIdx > 0 ? _recDates[_ownIdx - 1] : null;
      let _salary = 0, _spent = 0, _backStamped = 0, _expected = null;
      // A reading that found zero drift writes no row at all (cash.js appends only when
      // `delta !== 0`), so for drift 0 the row found above belongs to an EARLIER reading and the
      // window would be too wide. There is nothing to explain at zero, so it is skipped outright.
      if (_lo && _hi && known && drift !== 0) {
        _salary = (Array.isArray(s.cashTxs) ? s.cashTxs : []).reduce((a, t) => {
          if (!t || t._audit !== true || (t.currency || 'USD') !== 'TRY') return a;
          const dt = String(t.date || '');
          return (dt > _lo && dt <= _hi) ? a + (Number(t.amount) || 0) : a;
        }, 0);
        const cf = (s.cashflow && typeof s.cashflow === 'object') ? s.cashflow : {};
        Object.keys(cf).filter(k => /^\d{4}-\d{2}$/.test(k)).forEach(mk => {
          const items = (cf[mk] && Array.isArray(cf[mk].items)) ? cf[mk].items : [];
          items.forEach(it => {
            // `_deducted > 0` means the ledger DID move, so that one is not a gap.
            if (!it || !it.confirmed || (Number(it._deducted) || 0) > 0) return;
            const ca = String(it.confirmedAt || '');
            if (!(ca > _lo && ca <= _hi)) return;
            const amt = Number(it.amount) || 0;
            if (mk >= _lo.slice(0, 7) && mk <= _hi.slice(0, 7)) _spent += amt; else _backStamped += amt;
          });
        });
        _expected = +(_salary - _spent).toFixed(2);
      }
      const _residual = (known && _expected != null) ? +(drift - _expected).toFixed(2) : null;
      const _graded = (_residual != null) ? _residual : (known ? drift : null);
      const wide = _graded != null && Math.abs(_graded) > tol;
      const _explain = (_expected != null)
        ? ` · ${_n(_expected)} of it is salary not yet credited and income-funded expenses`
          + `${_backStamped ? ` (${_n(_backStamped)} of back-stamped items excluded)` : ''}`
          + `, leaving ${_residual > 0 ? '+' : ''}${_n(_residual)} unexplained`
        : ' · no earlier reading to measure the window against';
      add({
        id: 'cash.bankSplit',
        label: 'TRY cash vs banks',
        status: inconsistent ? CRITICAL : (wide ? WARN : OK),
        detail: inconsistent
          ? `${names.join(' + ')} = ${_n(sum)} but the reading was recorded as ${_n(recorded)}`
          : `${names.join(' + ')} = ${_n(sum)}` + (known
            ? ` · last correction ${drift > 0 ? '+' : ''}${_n(drift)}${_explain}`
            : ' · correction not recorded'),
        consequence: inconsistent
          ? 'The per-bank breakdown and the total it was recorded as disagree.'
          : (wide
            ? (_residual != null
              ? 'This much of the last correction is not explained by the salary mirror or by income-funded expenses, so something is moving money the app never sees.'
              : 'The app was this far from the banks at the last check and there is no earlier reading to measure the window against.')
            : ''),
        remedy: 'Money → Cash Transactions → Reconcile TRY.',
        page: 'cash',
      });
    }
    // ── Every balance against its own ledger ─────────────────────────────────
    // `cash.bankSplit` grades TRY against the BANK. This grades every currency against the
    // app's own cash ledger, which is a different question and the one nothing was asking.
    //
    // The invariant: `S.cash[cur]` equals the sum of non-audit `cashTxs` in that currency.
    // Audit rows are salary mirrors and carry no balance by design. Measured on live state
    // 2026-08-14: the drift was exactly 0.00 in TRY, USD and EUR — this is a real invariant,
    // not an approximation, because every mutator moves both legs together.
    //
    // Except one. `reconcileMo` appended two USD rows and never touched `S.cash`, and was
    // harmless for seven months only because the rows were +base*mul and -base*mul. TRY would
    // have self-healed — `backfillSalaryCashTxs` rebuilds any currency that carries audit rows
    // — but USD and EUR carry none, so a USD break is silent AND permanent. That is the shape
    // this check exists for: a writer that moves one leg. (MOVE-001, 2026-08-14)
    {
      const txs = Array.isArray(s.cashTxs) ? s.cashTxs : null;
      const cash = (s.cash && typeof s.cash === 'object') ? s.cash : null;
      const cut = s._truncated && s._truncated.cashTxs;
      const lmCommon = {
        label: 'Balances vs the cash ledger',
        consequence: 'A balance that disagrees with the rows it is built from means one leg of some write is missing. Every figure derived from cash — free TRY, liquidity, the capital identity, Total Wealth — inherits the gap, and only TRY is rebuilt on boot.',
        remedy: 'Money → Cash Transactions: find the movement with no row, or the row with no movement. Money → Reconcile TRY re-anchors TRY only.',
        page: 'cash',
      };
      if (!txs || !cash) {
        add({ ...lmCommon, id: 'cash.ledgerMatch', status: UNKNOWN, detail: 'no cash ledger to compare against' });
      } else if (cut) {
        // The 2,000-row cap drops the OLDEST rows, so the sum is legitimately short by
        // whatever was trimmed. Saying OK here would be a lie; saying WARN would be a false
        // alarm that never clears.
        add({ ...lmCommon, id: 'cash.ledgerMatch', status: UNKNOWN,
          detail: 'the ledger has been trimmed, so its sum is no longer the whole history' });
      } else {
        const sums = {};
        txs.forEach(t => {
          if (!t || t._audit === true) return;
          const cur = t.currency || 'TRY';
          sums[cur] = (sums[cur] || 0) + (Number(t.amount) || 0);
        });
        const curs = [...new Set([...Object.keys(cash), ...Object.keys(sums)])]
          .filter(c => /^[A-Z]{3}$/.test(c));
        // Same threshold backfillSalaryCashTxs uses to decide a TRY balance has drifted, so
        // the two cannot disagree about what counts as drift.
        const off = curs.map(c => ({ c, bal: Number(cash[c]) || 0, led: sums[c] || 0 }))
          .map(x => ({ ...x, d: x.bal - x.led }))
          .filter(x => Math.abs(x.d) > 1);
        if (!curs.length) {
          add({ ...lmCommon, id: 'cash.ledgerMatch', status: UNKNOWN, detail: 'no currency to compare' });
        } else if (off.length) {
          add({ ...lmCommon, id: 'cash.ledgerMatch', status: WARN,
            detail: off.map(x => `${x.c} balance ${_n(x.bal)} vs ledger ${_n(x.led)} (${x.d > 0 ? '+' : '−'}${_n(Math.abs(x.d))})`).join(' · ') });
        } else {
          add({ ...lmCommon, id: 'cash.ledgerMatch', status: OK,
            detail: `${curs.sort().join(', ')} each match their rows` });
        }
      }
    }

    // ── The plan's declaration against what actually converted ───────────────
    // ACCT-005, and the owner's decision on 2026-08-14: **the declaration stands, and the
    // divergence gets reported.** This is the reporting half.
    //
    // `reconcileMo` writes a `Monthly IBKR deposit` row of `base × mul` and — since `c9fe5be` —
    // credits `S.cash.USD` by it. Measured on the live book: seven reconciled months declared
    // $2,400 each, **$16,800 in total, against $0 of recorded `fx_convert`.** The conversions
    // happened at the bank and were never written down, so that row is the ONLY record in this
    // book that lira became dollars. The USD identity closes on it exactly:
    //
    //   +16,800.00 declaration · +5,479.26 trade_exit · +1,246.33 fx_convert · +13.20 transfer
    //   −23,138.17 portfolio_buy  =  +400.62  ≈  S.cash.USD 400.63
    //
    // Which is why "measure it instead" was the wrong answer to ACCT-005: removing the
    // declaration would leave USD cash at −$16,399. The model is sound. The risk is a month
    // where BOTH doors fire.
    //
    // August 2026 is the first such month — a ₺59,262.99 → $1,246.33 `fx_convert` on the 4th
    // (plus $13.20 transfer in) and no reconciliation yet. Reconciling it would credit another
    // $2,400 on top, for a month whose real conversion was $1,246.33. Two records, one event.
    {
      const txs = Array.isArray(s.cashTxs) ? s.cashTxs : null;
      const cut = s._truncated && s._truncated.cashTxs;
      const dwCommon = {
        label: 'Plan declaration vs what converted',
        consequence: 'The monthly row is both the record that dollars arrived AND the declaration of new outside capital. Where a real conversion is also recorded, the same money is counted twice — USD cash is overstated and contributed capital with it, which flows into Total Wealth P&L and XIRR.',
        remedy: 'Money → Cash Transactions: if the month already carries an fx_convert, do not reconcile it — or remove the conversion first, so the month has exactly one record of its funding.',
        page: 'cash',
      };
      if (!txs) {
        add({ ...dwCommon, id: 'capital.declaredVsWired', status: UNKNOWN, detail: 'no cash ledger to read' });
      } else if (cut) {
        add({ ...dwCommon, id: 'capital.declaredVsWired', status: UNKNOWN,
          detail: 'the ledger has been trimmed, so a month may have lost the record this compares' });
      } else {
        const mo = {};
        const bucket = k => (mo[k] || (mo[k] = { declared: 0, wired: 0 }));
        txs.forEach(t => {
          if (!t) return;
          const k = String(t.date || '').slice(0, 7);
          if (!/^\d{4}-\d{2}$/.test(k)) return;
          const note = String(t.note || '');
          if (/^monthly ibkr deposit/i.test(note)) bucket(k).declared += Number(t.amount) || 0;
          // What actually became dollars: a conversion leg crediting USD, or a declared
          // transfer in. Both record funding ARRIVING, which is what the monthly row also
          // claims to be.
          // ⚠ NOT EVERY CONVERSION IS THIS WIRE. This counted any TRY→USD conversion as plan
          // funding, which held only because all six in the book were IBKR wires. The Garanti
          // BBVA Kripto account is dollar-denominated too — lira in, dollars out — so a crypto
          // purchase booked honestly as a conversion would have been reported as money wired to
          // IBKR for the monthly plan, and this check would have declared the month funded.
          // `fxPurpose` is the controlled answer; its vocabulary and default live in
          // `capital.js` (FX_PURPOSE / fxPurposeOf) and the default is pinned to this line by
          // `a-conversion-has-a-destination.test.js`, because this module imports nothing.
          else if ((t.type === 'fx_convert' || /^transfer in\b/i.test(note))
            && (t.type !== 'fx_convert' || (t.fxPurpose || 'ibkr') === 'ibkr')
            && t.currency === 'USD' && (Number(t.amount) || 0) > 0) bucket(k).wired += Number(t.amount) || 0;
        });
        // ACCT-006. Comparing the WHOLE declaration against what converted overstated the gap,
        // because the $2,400 is a total across all assets and only part of it physically wires.
        // The owner's ruling, 2026-08-10: IBKR 60% / Garanti 38% / Garanti+Akbank 2% — and only
        // the IBKR share moves; the rest is already lira sitting at his own bank.
        //
        // DERIVED, not restated (the rule that closed `CURRENT_SCHEMA`, `RESTORE_KEYS`, the
        // deploy gate and `INTERNAL_TYPES`): the share is read off the live allocation targets,
        // so changing the plan changes this automatically. Live 2026-08-14: 27 planned holdings,
        // IBKR 60.00% of 100.00% → $1,440 of the $2,400. August converted $1,259.53, which is
        // 12.5% short of the wire — not the 48% that comparing against the total implies.
        //
        // No share derivable (no targets, or no IBKR holdings) → the expectation is omitted
        // rather than guessed, and the detail says only what it measured.
        const _planned = (s.holdings || []).filter(h => h && (Number(h.allocationPct) || 0) > 0);
        const _totPct = _planned.reduce((n, h) => n + (Number(h.allocationPct) || 0), 0);
        const _ibkrPct = _planned.filter(h => String(h.broker || '') === 'IBKR')
          .reduce((n, h) => n + (Number(h.allocationPct) || 0), 0);
        const _share = _totPct > 0 && _ibkrPct > 0 ? _ibkrPct / _totPct : null;
        const _expect = d => (_share == null ? null : d * _share);
        const _vs = (declared, wired) => {
          const e = _expect(declared);
          if (e == null || !(e > 0)) return '';
          const pct = Math.round(Math.abs(wired - e) / e * 100);
          const head = ` — the wiring share of it is ${_n(e)} (IBKR ${Math.round(_share * 100)}% of the plan), so this `;
          // 0% is neither short nor over, and saying "0% short" reads as a defect that is not
          // there. The three cases are named separately.
          return head + (pct === 0 ? 'matches the wiring share' : `is ${pct}% ${wired < e ? 'short' : 'over'}`);
        };

        // ⚠ "NO DEPOSIT ROW" IS NOT "NOT RECONCILED" ANY MORE, AND THAT IS THIS MODULE'S DOING.
        //
        // `armed` read a missing `Monthly IBKR deposit` row as a month that has not been
        // reconciled yet, which was true until `4a245e7` taught the deposit leg to look for a
        // plan-funding conversion before declaring one. September 2026 reconciled on that path and
        // wrote NOTHING, exactly as intended — and this check then reported it as
        //
        //     2026-09: 8,884 already converted and the month is not reconciled —
        //              reconciling it would credit the plan amount on top
        //
        // on a month whose `monthlyRates` status is 'reconciled' and that cannot be reconciled
        // again. The warning named the very outcome its own fix had prevented.
        //
        // A reconciled month that converted and declared nothing is funded exactly once, which is
        // the state this check exists to protect. It is not armed. Measured 2026-10-02: this is
        // September and only September; nothing else in the book is in that state.
        const _recon = k => {
          const r = (s.monthlyRates && s.monthlyRates[k]) || null;
          return !!r && r.status === 'reconciled';
        };
        const both = Object.entries(mo).filter(([, v]) => v.declared > 0 && v.wired > 0);
        const armed = Object.entries(mo).filter(([k, v]) => v.declared === 0 && v.wired > 0 && !_recon(k));
        const declaredMonths = Object.values(mo).filter(v => v.declared > 0).length;
        // A month that has converted but not reconciled has no declaration of its own yet, so
        // the expectation is taken from the plan the reconciliation WOULD write.
        const _planAmt = (Number(s.base) || 0) * (() => {
          const r = s.monthlyRates || {};
          const k = Object.keys(r).sort().pop();
          return (k && r[k] && Number(r[k].mul)) || 1;
        })();
        if (both.length) {
          add({ ...dwCommon, id: 'capital.declaredVsWired', status: WARN,
            detail: both.map(([k, v]) => `${k}: declared ${_n(v.declared)} AND converted ${_n(v.wired)} — both on the books${_vs(v.declared, v.wired)}`).join(' · ') });
        } else if (armed.length) {
          add({ ...dwCommon, id: 'capital.declaredVsWired', status: WARN,
            detail: armed.map(([k, v]) => `${k}: ${_n(v.wired)} already converted and the month is not reconciled — reconciling it would credit the plan amount on top${_planAmt > 0 ? _vs(_planAmt, v.wired) : ''}`).join(' · ') });
        } else {
          add({ ...dwCommon, id: 'capital.declaredVsWired', status: OK,
            detail: declaredMonths
              ? `${declaredMonths} declared month(s), none with a competing conversion record`
              : 'no month has been reconciled yet' });
        }
      }
    }

    // ── A BALANCE CANNOT BE NEGATIVE ─────────────────────────────────────────
    //
    // No account in this book has an overdraft. A negative cash balance therefore never
    // describes reality; it describes a debit the book made against money it never held.
    //
    // Measured 2026-09-10: EUR went to -1,973.74 because two SAP employee-plan lots were
    // debited from a EUR balance of zero. The plan is a payroll deduction and its money never
    // passes through any tracked account. Total Wealth read 1,973.74 low as a result, which is
    // why the owner saw it "not move at all" after entering 1,957 of shares.
    //
    // THE REASON THIS ROW IS CRITICAL AND NOT A WARNING. The remedy a reader reaches for is
    // Reconcile, and Reconcile writes the difference as a `deposit` -- so running it here would
    // book 1,973.74 of income that never arrived, and hand the same figure to netExtCapital as
    // capital arriving from outside. The error would stop being visible and start being
    // permanent. Find the wrong debit first. (audit 2026-09-10)
    {
      const bal = (s && s.cash) || {};
      const neg = Object.keys(bal).filter(k => Number(bal[k]) < -0.005)
        .map(k => `${k} ${Number(bal[k]).toFixed(2)}`);
      add({
        id: 'cash.noNegative',
        label: 'No cash balance is negative',
        status: neg.length ? CRITICAL : OK,
        detail: neg.length ? neg.join(' · ') : `${Object.keys(bal).length} balance(s) at or above zero`,
        consequence: neg.length
          ? 'Net worth is understated by the shortfall, and a reconciliation would convert it into '
            + 'income that never arrived rather than reveal the debit that caused it.'
          : '',
        remedy: 'Find the transaction that spent money the account never held and remove it. Do NOT reconcile first.',
        page: 'cash',
      });
    }

    // ── A MONTH THAT SPENT MORE THAN IT EARNED SAYS SO, OR NOBODY LEARNS IT ──
    //
    // `paidBy: 'income'` means "this came out of this month's salary". The salary is a MIRROR
    // row, so neither leg touches `S.cash` and the two net off -- a coherent model that held for
    // five months. It stops holding the month income does not cover the items, and then the
    // difference came out of the bank with nothing in the book saying so.
    //
    // Measured 2026-09-21: September confirmed TRY 260,909 of income-paid items against TRY
    // 234,094 of salary. TRY 26,815 left the account unrecorded, and the owner found it the
    // only way available to him -- by noticing the cash reconciliation was large and asking why.
    // April through August never breached, which is exactly why nothing was ever built to catch
    // it: the model is right until the first month it is not.
    //
    // ⚠ MEASURED AGAINST INCOME ALONE, not income minus the portfolio commitment. The modal's
    // "Est. Surplus" subtracts the commitment, which assumes salary funds it -- in September it
    // did not, the time deposit did. Subtracting it here would raise the alarm by six figures on
    // an assumption the ledger contradicts. This check states only what is certain.
    {
      const cf = (s && s.cashflow) || {};
      const over = [];
      Object.keys(cf).sort().forEach(k => {
        const mo = cf[k];
        if (!mo || !Array.isArray(mo.items)) return;
        const income = Number(mo.income) || ((mo.incomes || []).reduce((a, i) => a + (Number(i.amount) || 0), 0));
        if (!(income > 0)) return;
        const paid = mo.items.reduce((a, it) => a + ((it && it.confirmed && it.amount
          && (!it.paidBy || it.paidBy === 'income')) ? Number(it.amount) || 0 : 0), 0);
        // A withdrawal the owner recorded for that month IS the ledger saying so. Once the month
        // is closed the Paid-by select is disabled (cashflow.js, isClosed), so the only way left
        // to record "this came out of the bank" is Money → Cash → Withdraw, dated in the month.
        // Grade the remainder, not the gap: paid - income - what the ledger already records.
        // Reconciliation corrections are excluded: they are the absorption this check warns of,
        // not an explanation of it.
        const recorded = ((s && s.cashTxs) || []).reduce((a, t) => {
          if (!t || t.type !== 'withdraw' || (t.currency || 'TRY') !== 'TRY') return a;
          if (String(t.date || '').slice(0, 7) !== k) return a;
          if (/^Cash reconciliation/.test(String(t.note || ''))) return a;
          return a + Math.abs(Number(t.amount) || 0);
        }, 0);
        if (paid - income - recorded > 1) over.push({ k, gap: paid - income - recorded });
      });
      const worst = over.slice().sort((a, b) => b.gap - a.gap);
      add({
        id: 'cashflow.incomeOverflow',
        label: 'No month spent more from income than it earned',
        status: over.length ? WARN : OK,
        detail: over.length
          ? `${over.length} month(s) over: ` + worst.slice(0, 4).map(o => `${o.k} by \u20ba${_n(o.gap)}`).join(' \u00b7 ')
          : `${Object.keys(cf).length} month(s), every one inside its income`,
        consequence: over.length
          ? 'Items marked "paid from income" beyond what the month earned came out of the bank, and '
            + 'nothing in the ledger records it. Cash reads high until the next reconciliation absorbs '
            + 'the difference as an unexplained adjustment.'
          : '',
        remedy: 'Open month: Cash Flow → set Paid by = Savings → Cash on the items the salary did not cover. Closed month: Money → Cash → Withdraw (TRY, dated in that month, reason Living Expense) for the overflow amount. Either one writes the withdrawal instead of leaving it to the next reconciliation.',
        page: 'cashflow',
      });
    }

    // ── `cashflow.noPaidBy` WAS HERE, AND IT WAS A FALSE POSITIVE ────────────
    //
    // It reported every item with no `paidBy` as an expense with no declared source: 7 rows and
    // TRY 134,607 on the live book, including rows the owner was looking at while the screen said
    // "income" on all of them. He was reading the screen correctly.
    //
    // `paidBy` is OPTIONAL and its absence is read as 'income' at four sites, identically:
    //
    //     cashflow.js:216    return !it.paidBy || it.paidBy === 'income';
    //     cashflow.js:998    const pb = it.paidBy || 'income';     <- what the select displays
    //     cashflow.js:1070   (!it.paidBy || it.paidBy === 'income')   paid/income totals
    //     cashflow.js:1074   (!it.paidBy || it.paidBy === 'income')   the projection
    //
    // and `cfToggle:1221` writes `pbSel.value`, which IS that displayed default. So "absent" and
    // "'income'" are one state by design, and a check that grades the absence contradicts the
    // module it is checking. The removed docblock even quoted line 216 and then concluded the
    // opposite: that being counted as income-paid without an explicit field was the defect.
    //
    // The real risk it was reaching for is already measured, correctly, by `cashflow.incomeOverflow`
    // directly above: income-funded spending BEYOND what the month earned is money that left the
    // bank with no ledger row. That is a quantity, not a missing field, and it is firing on
    // 2026-09 by TRY 40,720. Nothing is lost by deleting this one.
    //
    // (removed 2026-10-02; added the same day in 55e01d3)

    // ── LOTS AND AVERAGED BASIS MUST DESCRIBE THE SAME POSITION ──────────────
    //
    // `hTotUSD` prefers `qty * avgCost` and treats lots as additive provenance, so a holding can
    // carry basis no lot describes -- ASELS held TRY 41,736 that way -- and it can also carry
    // lots that claim MORE than the position holds, which is the shape a partial exit leaves.
    //
    // Until 2026-10-02 the capital doors returned early on any holding with a lot, so the first
    // case was invisible: one `+ Lot` against a seeded position deleted its whole basis from
    // contributed capital and took a5 from 116% of band to 417%. The door now credits the
    // difference. This check exists so that crediting it cannot SWALLOW the second case: a
    // negative gap reduces contributed capital just as quietly as the positive one inflated it.
    {
      const hs = Array.isArray(s.holdings) ? s.holdings : [];
      const fx = Number(s.fxRate) > 0 ? Number(s.fxRate) : null;
      const rough = (v, cur) => {
        if (cur === 'USD') return v;
        if (!fx) return null;
        if (cur === 'TRY') return v / fx;
        if (cur === 'EUR') return Number(s.fxRateEUR) > 0 ? (v * Number(s.fxRateEUR)) / fx : null;
        return null;
      };
      const gaps = [];
      hs.forEach(h => {
        if (!h || h.type === 'Cash' || !(h.qty > 0)) return;
        const lots = (h.lots || []).filter(l => l && l.type !== 'drip');
        if (!lots.length) return;
        if (!(h.avgCost > 0)) return;
        const lotBasis = lots.reduce((a, l) => a + (Number(l.cost) || (Number(l.qty) || 0) * (Number(l.price) || 0)), 0);
        const d = rough((Number(h.qty) || 0) * Number(h.avgCost) - lotBasis, h.currency || 'USD');
        if (d == null || !isFinite(d) || Math.abs(d) <= 50) return;
        gaps.push({ t: h.ticker, usd: d });
      });
      const neg = gaps.filter(g => g.usd < 0);
      add({
        id: 'capital.lotBasisGap',
        label: 'Lots and averaged basis describe the same position',
        status: !fx ? UNKNOWN : (neg.length ? WARN : OK),
        detail: !fx ? 'no FX rate to compare in one currency'
          : gaps.length
            ? `${gaps.length} holding(s) carry basis the lots do not describe: `
              + gaps.sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd)).slice(0, 6)
                .map(g => `${g.t} ${g.usd > 0 ? '+' : ''}${_n(g.usd)}`).join(' · ')
            : 'every lot-bearing holding\u2019s lots account for its whole basis',
        consequence: neg.length
          ? 'A holding whose lots claim MORE basis than it holds reduces contributed capital by the '
            + 'difference, which inflates every return percentage computed against it. The usual cause '
            + 'is a partial exit that cut the quantity and left the lots behind.'
          : '',
        remedy: 'Portfolio: compare the position\u2019s lots against its average cost. A positive gap is an opening balance and is counted as one; a negative gap is a lot that should have gone when the shares did.',
        page: 'portfolio',
      });
    }

    // ── A CONVERSION WITHOUT A DESTINATION IS READ AS THE IBKR WIRE ──────────
    //
    // `fxPurpose` says where a conversion's proceeds went. Two readers ask -- `cashflow.js`
    // for the plan transfer, and this file for what actually became dollars -- and both fall
    // back to `ibkr` when the field is absent, because that is what every conversion in the
    // book was before the field existed and the fallback has to preserve history.
    //
    // The fallback is therefore SILENT and CONSEQUENTIAL: an unstamped Kripto conversion reads
    // as money wired to IBKR for the monthly plan, and the month reports itself funded. v58
    // stamped the six historical legs, so this count is zero on a current book and can only
    // rise through a row written by an older build or resurrected by a merge -- which is
    // exactly when the default is doing work nobody chose.
    {
      const txs = Array.isArray(s.cashTxs) ? s.cashTxs : null;
      const VALID = ['ibkr', 'crypto', 'other'];
      const bare = txs ? txs.filter(t => t && t.type === 'fx_convert' && VALID.indexOf(t.fxPurpose) === -1) : [];
      add({
        id: 'cash.fxPurpose',
        label: 'Every currency conversion says where the money went',
        status: !txs ? UNKNOWN : (bare.length ? WARN : OK),
        detail: !txs ? 'no cash ledger to read'
          : bare.length
            ? `${bare.length} conversion leg(s) with no destination: ` + bare.slice(0, 6).map(t => `${String(t.date || '').slice(0, 10)} ${t.currency} ${_n(t.amount)}`).join(' · ')
            : `${txs.filter(t => t && t.type === 'fx_convert').length} conversion leg(s), all labelled`,
        consequence: bare.length
          ? 'An unlabelled conversion is read as the monthly IBKR wire. A conversion made for any '
            + 'other reason — the Garanti BBVA Kripto account is dollar-denominated too — then reports '
            + 'that month as funded by money that went somewhere else entirely.'
          : '',
        remedy: 'Money → Cash Transactions: delete the conversion and record it again, choosing a destination.',
        page: 'cash',
      });
    }

    // ── A CASH ROW HAS NO DIVIDEND TO REINVEST ───────────────────────────────
    //
    // On a Cash holding `qty` is the currency amount and `price` is the FX rate. A drip lot
    // there adds currency the account never received and averages a share price into an
    // exchange rate. Measured the same day: the July and August SAP dividends were entered on
    // the EUR row, which gained 4.637729 of quantity and saw its average rate go from 51.043 to
    // 51.745 because 171.679 and 186.666 were blended in as lira per euro.
    {
      const hs = Array.isArray(s.holdings) ? s.holdings : [];
      const bad = [];
      hs.forEach(h => {
        if (!h || h.type !== 'Cash') return;
        const d = (h.lots || []).filter(l => l && l.type === 'drip');
        if (d.length) bad.push(`${h.ticker}: ${d.length} drip lot(s), ${d.map(l => l.date).join(', ')}`);
      });
      add({
        id: 'lot.dripOnCash',
        label: 'No cash row carries a DRIP',
        status: bad.length ? CRITICAL : OK,
        detail: bad.length ? bad.slice(0, 3).join(' · ') : 'none',
        consequence: bad.length
          ? 'That row holds currency it never received and its average cost is an exchange rate with '
            + 'a share price averaged into it. The dividend is also missing from the holding that paid it.'
          : '',
        remedy: 'Portfolio → the cash row → remove the drip lot, then enter it on the holding that paid the dividend.',
        page: 'portfolio',
      });
    }

    const g = gradeAge(parseTs(rec && rec.at), now, 14 * DAY, 45 * DAY);
    add({
      id: 'cash.reconcileAge',
      label: 'TRY cash reconciliation',
      status: g.status,
      ageMs: g.ageMs,
      detail: g.status === UNKNOWN ? 'never run' : ago(g.ageMs),
      consequence: g.status === OK
        ? ''
        : 'Household spending does not debit this figure, so it drifts upward between reconciliations.',
      remedy: 'Read the balances off the banks and run Reconcile TRY.',
      page: 'cash',
    });
  }

  // ── Tax refund → cash ────────────────────────────────────────────────────
  // Engine snapshots live in their own IDB store and never enter S, by design. The cost of
  // that separation showed on 2026-08-10: a refund landed in the bank, the tax page said
  // REFUND PAID with the amount, the date and the EFT reference, and every cash, surplus,
  // free-TRY and capacity figure in the app was that much short with nothing on any screen
  // saying so. Passed IN for the same reason capital is — this file imports nothing.
  {
    const refunds = (opts && Array.isArray(opts.taxRefunds)) ? opts.taxRefunds : null;
    if (!refunds) {
      add({
        id: 'tax.refundBooked',
        label: 'Tax refunds in cash',
        status: UNKNOWN,
        detail: 'tax snapshots not read this session',
        consequence: 'A refund that has been paid but never booked leaves every cash figure short by its amount.',
        remedy: 'Open Tools → Tax so the snapshots load, then reload.',
        page: 'tax',
      });
    } else {
      const oti = Array.isArray(s.oneTimeIncome) ? s.oneTimeIncome : [];
      const unbooked = refunds.filter(r => !taxRefundBooking(oti, r));
      const oldest = unbooked.reduce((mx, r) => {
        const t = parseTs(r.paidDate);
        return t != null && (mx == null || t < mx) ? t : mx;
      }, null);
      const ageMs = oldest == null ? null : now - oldest;
      add({
        id: 'tax.refundBooked',
        label: 'Tax refunds in cash',
        // WARN is the exception, not the default. Only a refund paid inside the last week is
        // merely "not got round to it"; an age that will not compute, a paid date well in the
        // future and anything older all mean the money has been missing from every cash
        // figure for a while, and an ungradeable one must not be the QUIETEST state.
        // The lower bound is -DAY, not 0: paid dates are calendar days parsed as UTC midnight
        // and the owner is on UTC+3, so a refund paid TODAY reads as negative age until 03:00
        // local. The anchor code was fixed for this same nightly window.
        status: !unbooked.length ? OK
          : ((ageMs != null && ageMs >= -DAY && ageMs <= 7 * DAY) ? WARN : CRITICAL),
        ageMs,
        detail: !unbooked.length
          ? (refunds.length
            ? refunds.map(r => r.year + ' ' + _n(r.amount)).join(' · ') + ' booked'
            : 'no refund paid yet')
          : unbooked.map(r => r.year + ' ' + _n(r.amount) + ' paid ' + r.paidDate).join(' · ') + ' — not in cash',
        consequence: !unbooked.length ? ''
          : 'Cash, monthly surplus, free TRY and every capacity figure that divides by them are short by this amount.',
        remedy: 'Tools → Tax → Record in cash, on the validated year.',
        page: 'tax',
      });
    }
  }

  // ── Snapshots: volatility, drawdown and "daily change" all derive from these ──
  {
    const snaps = Array.isArray(s.snapshots) ? s.snapshots : [];
    // Take the max, never snaps[len-1]: the array was found unsorted in live state, which
    // is precisely the bug this check exists to catch. (audit 2026-08-01)
    const lastDate = snaps.reduce((mx, x) => {
      const d = x && x.date;
      return d && (!mx || String(d) > String(mx)) ? d : mx;
    }, null);
    const g = gradeAge(parseTs(lastDate), now, 3 * DAY, 7 * DAY);
    add({
      id: 'snapshots.gap',
      label: 'Wealth snapshots',
      status: snaps.length < 2 ? UNKNOWN : g.status,
      ageMs: g.ageMs,
      detail: snaps.length < 2 ? `${snaps.length} snapshot(s) — not enough to derive anything` : ago(g.ageMs),
      consequence: 'Volatility, drawdown and the "daily change" figure are computed from this series.',
      remedy: 'Open the Dashboard to record today\'s snapshot.',
      page: 'dash',
    });
  }

  // ── Rebalancing drift cache: feeds a money amount (targetUSD) and a status badge ──
  {
    const g = gradeAge(parseTs(s.rebalancing && s.rebalancing.lastDriftTs), now, 12 * HOUR, 3 * DAY);
    add({
      id: 'drift.age',
      label: 'Allocation drift snapshot',
      status: (s.rebalancing && s.rebalancing.lastDrift) ? g.status : UNKNOWN,
      ageMs: g.ageMs,
      detail: g.status === UNKNOWN ? 'not computed this session' : ago(g.ageMs),
      consequence: 'Rebalance amounts and the "on track / review due" badge come from it.',
      remedy: 'Open the Portfolio page to recompute.',
      page: 'portfolio',
    });
  }

  // Severity ordering, so two gradings can be combined without inventing a third rule.
  // (declared here rather than inline: the signals check needs it, and nothing else may.)
  // ── Signals feed ─────────────────────────────────────────────────────────
  // Three different facts were being reported as one. `_updatedAt` is the moment WE last
  // wrote a signal row, so max(_updatedAt) answers "how long since a report we had never seen
  // arrived" — and the label read "Signals feed · 4d ago" with a consequence implying the
  // pull was stale, while the pull had in fact succeeded a minute earlier and simply found
  // nothing new. A pipeline timestamp and a publication date must never render the same.
  //
  // So: the PULL is graded on when we last talked to the feed, and the newest report date is
  // reported beside it as the separate fact it is. An analyst who publishes weekly is not a
  // broken integration. (audit 2026-08-03)
  {
    const sig = Array.isArray(s.signals) ? s.signals : [];
    const pull = s._lastSignalPull || null;

    // sourceDate is NOT one format. 150 mongo rows carry '2026-07-30'; 18 older imports carry
    // 'May 4' with no year at all. String-comparing the two ranks 'May 4' above every ISO date
    // ('M' > '2'), which is exactly how the first version of this check reported the newest
    // report as May — three months adrift — while the real newest was four days old. Parse or
    // discard; never rank a value whose format is not guaranteed. (audit 2026-08-03)
    let newestTs = null, undatedCount = 0;
    sig.forEach(x => {
      const raw = x && (x.sourceDate || x.createdAt);
      // ISO date or a numeric timestamp, nothing else. Date.parse('May 4') happily returns
      // May 4 of the CURRENT year — an inferred year is a fabricated fact, and the whole
      // point here is not to rank a value whose format is not guaranteed.
      const str = String(raw == null ? '' : raw);
      const t = typeof raw === 'number' ? parseTs(raw)
        : /^\d{4}-\d{2}-\d{2}/.test(str) ? Date.parse(str.slice(0, 10))
          : null;
      if (t == null || isNaN(t)) { if (raw) undatedCount++; return; }
      if (newestTs == null || t > newestTs) newestTs = t;
    });

    const gPull = gradeAge(parseTs(pull && pull.at) || null, now, 1 * DAY, 3 * DAY);
    // The content has its own clock. A pull that succeeds every three hours against a source
    // that stopped publishing a month ago is a green check over a dead feed — the same error
    // as the one above, pointing the other way.
    const gRep = gradeAge(newestTs, now, 14 * DAY, 45 * DAY);
    const worst = [gPull.status, newestTs != null ? gRep.status : UNKNOWN]
      .reduce((a, b) => (RANK[b] > RANK[a] ? b : a), OK);

    add({
      id: 'signals.age',
      label: 'Signals pull',
      status: pull ? worst : UNKNOWN,
      ageMs: gPull.ageMs,
      detail: (pull ? 'pulled ' + ago(gPull.ageMs) : 'no pull recorded yet')
        + (sig.length
          ? ` · ${sig.length} held, newest report ${newestTs != null ? ago(gRep.ageMs) : 'undated'}`
            + (undatedCount ? ` · ${undatedCount} with an unparseable date` : '')
          : ' · no signals held'),
      consequence: 'Price targets and support levels on the Signals page come from this pull. A quiet feed and a broken one look the same until the pull time is separated from the report date — and both are graded here, because a healthy pull against a dead source is still a dead source.',
      remedy: pull ? 'If the pull is recent and the newest report is not, the upstream pipeline stopped producing — check it rather than the app.' : 'Open Signals and pull the feed.',
      page: 'signals',
    });
  }

  // ── Retention trims ──────────────────────────────────────────────────────
  // ELEVEN caps drop records by design: the seven-array `_CAPS` loop, the history merge (1,000),
  // the mongo-signal prune (150), and two nested AI arrays. This is the surface for that record
  // — a diagnostic nobody can see is the same as no diagnostic.
  //
  // Until 2026-08-15 it only saw four of the eleven. The `_CAPS` loop wrote `S._truncated[k]`,
  // which is consumed for `cashTxs` and nothing else, and never called `_recordTrim` — so a
  // trim of the other six was written down and read by no one, while this check went on saying
  // "nothing trimmed this session". Live at the moment that was found: `_truncated` held
  // `notifications −1` from minutes earlier, and `__zagxTrims` did not mention it. An
  // affirmative OK over a deletion is worse than no check.
  //
  // Trimming is NOT a fault in itself, so the normal state is OK with the counts named. What
  // decides the grade is whether the dropped record can come back. The old copy said only
  // `history` could not — but `history` is one of FIVE. Signals regenerate from the Mongo feed
  // and the AI arrays from the next scan; the money arrays regenerate from nothing at all, and
  // the trim reaches the cloud on the same save, so the other side cannot hand them back.
  {
    // Keyed by the name each trim records under. The value is what is lost, in the owner's terms.
    const NONREGEN = {
      history: 'the app\'s own audit log, which is stripped from the cloud payload',
      cashTxs: 'cash ledger rows',
      realized: 'closed-trade P&L, which is the tax record',
      rfTxs: 'reserve fund movements',
      dividends: 'dividend records',
    };
    const trims = (opts && opts.trims) || null;
    const keys = trims ? Object.keys(trims).filter(k => trims[k] && trims[k].dropped > 0) : [];
    const totalDropped = keys.reduce((a, k) => a + trims[k].dropped, 0);
    // `_lastTrims` is module-level: it dies with the tab. `S._truncated` persists. A cap that
    // fired yesterday is gone from the first and recorded in the second, and the loss it
    // records is permanent — so reading only the session's trims would report "nothing
    // trimmed" about data that is already unrecoverable. Session keys win, to avoid counting
    // the same trim twice now that `_CAPS` writes both.
    const past = (s && s._truncated && typeof s._truncated === 'object') ? s._truncated : {};
    const pastKeys = Object.keys(past).filter(k => past[k] && past[k].dropped > 0 && keys.indexOf(k) < 0);
    const lost = [...keys, ...pastKeys].filter(k => NONREGEN[k]);
    const undated = keys.filter(k => past[k] && past[k].undated > 0);
    const parts = [];
    if (keys.length) {
      parts.push(keys.map(k => `${k} −${trims[k].dropped}`).join(' · ') + ` (${totalDropped} record(s) this session)`);
    }
    if (pastKeys.length) {
      parts.push('earlier: ' + pastKeys.map(k => `${k} −${past[k].dropped}`).join(' · '));
    }
    if (undated.length) {
      parts.push(undated.map(k => `${k} carries ${past[k].undated} undated row(s), kept regardless`).join(' · '));
    }
    add({
      id: 'retention.trims',
      label: 'Retention caps',
      status: trims ? (lost.length ? WARN : OK) : UNKNOWN,
      detail: !trims ? 'not measured'
        : !parts.length ? 'nothing trimmed this session'
          : parts.join(' · '),
      consequence: lost.length
        ? 'A cap dropped ' + lost.map(k => NONREGEN[k]).join(', ') + '. Nothing regenerates these — the trim reaches the cloud on the same save, so the other device cannot hand them back either.'
        : 'Caps keep the state small. What they dropped is regenerable: signals from the feed, AI arrays from the next scan.',
      remedy: lost.length
        ? 'Export a backup now, then raise the cap for that array in core/state.js (HISTORY_CAP, or the _CAPS map) before the next save trims further.'
        : 'Nothing to do while the counts stay in the range the caps were sized for.',
      page: 'integrity',
    });
  }

  // ── Capital reconciliation ───────────────────────────────────────────────
  // Passed IN rather than computed here, because this file imports nothing and capital.js
  // needs FX and a cost basis. Absent = UNKNOWN, per the governing rule of this module: an
  // input whose validity cannot be established is never reported as fine.
  {
    const cap = opts && opts.capital;
    if (!cap) {
      add({
        id: 'capital.reconciliation',
        label: 'Capital reconciliation',
        status: UNKNOWN,
        detail: 'not computed this session',
        consequence: 'NET GROWTH, the VOO benchmark and every return percentage divide by a capital figure nobody has checked.',
        remedy: 'Open the Dashboard to recompute.',
        page: 'dashboard',
      });
    } else {
      const nUnknown = (cap.unknown || []).length;
      add({
        id: 'capital.reconciliation',
        label: 'Capital reconciliation',
        status: cap.reconciled ? OK : CRITICAL,
        detail: cap.reconciled
          ? `assets $${Math.round(cap.assetsAtCostUSD)} explained within $${Math.round(cap.tolerance)}`
          : (cap.reasons || []).join('; ') || 'does not close',
        consequence: cap.reconciled ? ''
          : 'Every return percentage divides by capital. While this does not close, NET GROWTH and the benchmark alpha are suppressed rather than shown wrong.',
        remedy: 'Cash → label the unclassified rows, then reload.',
        page: 'cash',
      });
      if (nUnknown) {
        const worst = (cap.unknown || []).slice()
          .sort((a, b) => Math.abs(b.usd || 0) - Math.abs(a.usd || 0))[0];
        add({
          id: 'capital.unclassified',
          label: 'Unclassified cash rows',
          status: CRITICAL,
          detail: `${nUnknown} row(s); largest ${worst.currency || ''} ${Math.round(worst.amount || 0)} on ${String(worst.date || '').slice(0, 10)} — "${(worst.note || '(no note)').slice(0, 40)}"`,
          consequence: 'These are neither counted as capital nor ignored — they are held out, because guessing is what produced the wrong numbers before.',
          remedy: 'Cash → open each row and set what it was.',
          page: 'cash',
        });
      }
    }
  }

  // ── Net worth: one definition, N callers ─────────────────────────────────
  //
  // ⚠ CONTRACT-003 DELETED THE SECOND DEFINITION AND LEFT THE THIRD STANDING.
  //
  // `goals.js` kept its own `_twUSD()` and read **$6,080.17 / 7.79 % low** for five weeks; the
  // fix routed it through `risk-calc.netWorthUSD()`. But that function is a THREE-TIER fallback:
  // the injected provider (`computeTotalWealthUSD`, wired at `main.js:43`), then
  // `window.__twUSD`, then `grossHoldingsUSD() + cash`. That last tier is a net worth with no
  // reserve fund, no accrued time-deposit interest and no unpriced holdings — which is the
  // CONTRACT-003 formula, still in the tree, one failed injection away from being the answer.
  //
  // `window.__twUSD` is no safer: `dashboard.js` writes it only when the Dashboard RENDERS, so a
  // cold boot into any other page leaves it at a default-state value (`portfolio.js:393` records
  // exactly that incident).
  //
  // Nothing announces a fallback. Every consumer — Open Risk %, the goals ring, the trade sizer —
  // would simply be handed a smaller number. So this asks the one question that discriminates:
  // **does the figure risk-calc hands out equal the canonical one?**
  //
  // A cross-page equality assertion was the check owed for CONTRACT-003 (register, owed #15).
  // Comparing the Goals page to the Dashboard would now compare a number to itself, the duplicate
  // being deleted; comparing the ACCESSOR to the SOURCE is the same question with its failure
  // power left in.
  {
    const nw = opts && opts.netWorth;
    const nwCommon = {
      label: 'Net worth, one definition',
      consequence: 'Open Risk %, the goals ring and the trade sizer all divide by this number. A silent fallback makes every one of them wrong in the same direction, with nothing on screen to say so.',
      remedy: 'Reload. If it persists, registerNetWorth is not wiring at boot.',
      page: 'dashboard',
    };
    if (!nw || typeof nw.canonical !== 'number' || typeof nw.viaRiskCalc !== 'number') {
      add({ ...nwCommon, id: 'netWorth.singleSource', status: UNKNOWN,
        detail: 'not computed this session' });
    } else if (nw.providerWired === false) {
      add({ ...nwCommon, id: 'netWorth.singleSource', status: CRITICAL,
        detail: 'registerNetWorth did not wire — risk-calc is answering from a fallback' });
    } else {
      // A tenth of a percent, floored at a dollar. Tighter than the capital band on purpose:
      // these two are the SAME computation reached two ways, so any real gap is a defect and the
      // tolerance exists only for float noise.
      const tol = Math.max(1, Math.abs(nw.canonical) * 0.001);
      const gap = Math.abs(nw.canonical - nw.viaRiskCalc);
      add({ ...nwCommon, id: 'netWorth.singleSource',
        status: gap <= tol ? OK : CRITICAL,
        detail: gap <= tol
          ? `$${Math.round(nw.canonical)} both ways`
          : `Dashboard $${Math.round(nw.canonical)} vs risk-calc $${Math.round(nw.viaRiskCalc)} — $${Math.round(gap)} apart`,
      });
    }
  }

  // ── Currency effect on cost basis ────────────────────────────────────────
  // Not a health check in the usual sense: the FX effect is a fact about the position, not a
  // fault. What IS a fault is the part that cannot be computed, because a cost basis with no
  // date makes the question unanswerable — and an unanswerable question is what leaves the
  // capital reconciliation with a residual nobody can attribute.
  {
    const fx = opts && opts.fx;
    if (fx) {
      const cov = Math.round(fx.coveragePct || 0);
      const undated = (fx.undated || []).length;
      const worst = (fx.undated || []).slice().sort((a, b) => (b.costLocal || 0) - (a.costLocal || 0))[0];
      add({
        id: 'fx.costBasisDates',
        label: 'Cost basis with a purchase date',
        // UNDATED IS NEVER OK, whatever the percentage. Coverage hit 96.5% with two items
        // still uncomputable, the check read OK, and OK checks are filtered out of the
        // action list — so the very button that would have fixed them stopped rendering.
        // A threshold that hides the last mile is worse than no threshold: it reports
        // "healthy" precisely when one click away from actually being healthy.
        // (audit 2026-08-03)
        status: !undated ? OK : cov >= 90 ? WARN : CRITICAL,
        detail: undated
          ? `${cov}% dated · ${undated} position(s) undated, largest ${worst.ticker} ${worst.currency} ${Math.round(worst.costLocal)} (${worst.reason})`
          : `${cov}% dated`,
        consequence: undated
          ? 'Their currency effect cannot be computed at all, so part of the capital residual can never be attributed.'
          : '',
        remedy: 'Portfolio → add dated lots, or complete the month in Monthly.',
        page: 'portfolio',
      });
      const declPct = Math.round(fx.declaredPct || 0);
      add({
        id: 'fx.effect',
        label: 'Currency effect on dated cost',
        status: OK,   // a measurement, not a fault
        detail: `${fx.effectUSD >= 0 ? '+' : '−'}$${Math.abs(Math.round(fx.effectUSD))} on $${Math.round(fx.atPurchaseUSD)} of dated cost`
          + (declPct > 0 ? ` · ${declPct}% rests on the declared opening month ${fx.basisMonth || ''}` : ''),
        consequence: '',
        remedy: declPct > 0 ? 'Settings → change the opening month if that is not when you entered those positions.' : '',
        page: 'dashboard',
      });
    }
  }

  // ── Employer plan contributions ──────────────────────────────────────────
  // A monthly payroll plan that stops appearing is indistinguishable from a plan that was
  // never recorded. Both leave the cost basis and the match income quietly frozen.
  {
    const plan = holdings.filter(h => h && (h.lots || []).some(l => l && l.type === 'investment'));
    if (plan.length) {
      const newest = plan.reduce((mx, h) => (h.lots || []).reduce((m, l) => {
        const t = parseTs(l && l.date);
        return t != null && t > m ? t : m;
      }, mx), 0);
      const g = gradeAge(newest || null, now, 45 * DAY, 75 * DAY);
      add({
        id: 'plan.contributions',
        label: 'Employer plan contributions',
        status: g.status,
        ageMs: g.ageMs,
        detail: g.status === UNKNOWN ? 'no dated lot' : `last lot ${ago(g.ageMs)}`,
        consequence: 'Missing months understate the plan cost basis, the employer match and total net worth.',
        remedy: 'Portfolio → add the month\'s investment and match lots.',
        page: 'portfolio',
      });
    }
  }

  // ── Broker fee model ─────────────────────────────────────────────────────
  // Commission was never recorded at all until 2026-08-03, and all thirteen IBKR positions
  // understated cost by $39 in total as a result. The charge is now measured per broker —
  // min($1.00, 1% of value) at IBKR, nothing on TEFAS or the FX legs — but BIST equities have
  // never been checked against a bank debit. An unverified fee model is reported here rather
  // than silently treated as zero, because zero is a measurement and absent is a gap, and
  // cost is the denominator of every return the app prints.
  //
  // ALWAYS ADDED, both of them. Until 2026-08-03 these two were pushed only when they FAILED,
  // so the moment the last venue was measured the check vanished and the ring went from 16/17
  // to 16/16. That is the exact defect this page was built to fix — the capital reconciliation
  // disappearing from the UI at the instant it started passing. A denominator that moves makes
  // "16/16" unreadable: tomorrow a new broker appears and it says 17/17, and nothing tells you
  // which of those two was the improvement. A passing check keeps its row and shows its margin.
  {
    const unknownFees = (opts && opts.fees) || null;
    const heldTotal = (Array.isArray(s.holdings) ? s.holdings : []).filter(h => h && (h.qty || 0) > 0).length;
    if (unknownFees && unknownFees.length) {
      add({
        id: 'fees.model',
        label: 'Unverified broker fee',
        status: UNKNOWN,
        detail: `${unknownFees.length} held position(s) — ${unknownFees.slice(0, 3).map(x => x.ticker).join(', ')}${unknownFees.length > 3 ? '…' : ''} at ${unknownFees[0].broker}`,
        consequence: 'Purchases are recorded at the quoted price. If that broker charges a commission, cost basis is low and every return percentage is high.',
        remedy: 'Check one bank debit against the recorded amount, then set the model.',
        page: 'portfolio',
      });
    } else if (unknownFees) {
      add({
        id: 'fees.model',
        label: 'Broker fee models',
        status: OK,
        detail: `${heldTotal}/${heldTotal} held position(s) charged under a measured model`,
        consequence: 'Purchases are recorded at the quoted price. If that broker charges a commission, cost basis is low and every return percentage is high.',
        remedy: 'Re-check a bank debit whenever a broker changes its schedule.',
        page: 'portfolio',
      });
    }
    // Exits are charged too, and a trade with no broker cannot be charged at all. All eight
    // open positions are BIST equities with broker:null, so their exits book gross proceeds.
    // Correct — a guessed commission in realized P&L would be worse — but it is a gap, and a
    // gap has to be visible. (audit 2026-08-03)
    const unknownTrades = (opts && opts.feeTrades) || null;
    const openTotal = (Array.isArray(s.trades) ? s.trades : [])
      .filter(t => t && !t._del && t.status !== 'Closed' && t.type !== 'TimeDeposit').length;
    if (unknownTrades && unknownTrades.length) {
      add({
        id: 'fees.trade-exit',
        label: 'Exit commission cannot be modelled',
        status: UNKNOWN,
        detail: `${unknownTrades.length} open trade(s) with no broker recorded — ${unknownTrades.slice(0, 3).map(x => x.ticker).join(', ')}${unknownTrades.length > 3 ? '…' : ''}`,
        consequence: 'Exits book proceeds at the quoted price. Realized P&L on those trades will read high by the exit commission.',
        remedy: 'Record the broker on the trade, then the existing model charges both sides.',
        page: 'trades',
      });
    } else if (unknownTrades) {
      add({
        id: 'fees.trade-exit',
        label: 'Exit commission',
        status: OK,
        detail: openTotal ? `${openTotal}/${openTotal} open trade(s) name a broker; exits are charged both sides`
          : 'no open trades',
        consequence: 'Exits book proceeds at the quoted price. Realized P&L on those trades will read high by the exit commission.',
        remedy: 'Record the broker on any new trade, or the model falls back to unknown.',
        page: 'trades',
      });
    }
  }

  // ---- CUT, BUT STILL HELD ------------------------------------------------------------
  // "How will the system know I exited these?" — the owner, 2026-08-04, and the honest answer
  // was that it would not. Setting a target to 0% says what the policy is; skipping the row in
  // the monthly plan says "not bought this month". Neither says "sold". Only a recorded exit
  // does, and until 2026-08-04 the exit dialog refused every holding without a lot list —
  // forty-two of forty-four.
  //
  // So this is the check that closes the loop: a target of zero with quantity still on the
  // books is an intention that has not been executed, or an execution that has not been
  // recorded. Both are worth seeing, and the difference between them is not something the app
  // can know. It reads UNKNOWN-free by design: the state is either "0% and holding" or not.
  // (audit 2026-08-04)
  {
    // SCOPED TO SLEEVES THAT ACTUALLY RUN TARGETS. A 0% target only means "cut" where there
    // is a target regime to be cut from. SAP XETRA and Crypto-Medium Term carry
    // allocationPct:0 on every row because they have no targets at all — their weights sum to
    // zero, not to 100. Reading those four as "cut but still held" would have turned a
    // 13-position finding into a 17-position one, four of them positions the owner has no
    // intention of selling. A watchdog that cries wolf about the same four rows every day is
    // one the owner stops reading. If he ever sets targets on another sleeve, that sleeve
    // starts being checked automatically. (audit 2026-08-04)
    const _pfSum = {};
    (Array.isArray(s.holdings) ? s.holdings : []).forEach(h => {
      if (!h) return;
      const k = h.portfolioId || '_';
      _pfSum[k] = (_pfSum[k] || 0) + (h.allocationPct || 0);
    });
    const cut = (Array.isArray(s.holdings) ? s.holdings : [])
      .filter(h => h && h.allocationPct === 0 && (h.qty || 0) > 0 && h.type !== 'Cash'
        && (_pfSum[h.portfolioId || '_'] || 0) > 0)
      .map(h => h.ticker);
    const common = {
      id: 'positions.cutNotExited',
      label: 'Positions cut to 0% but still held',
      consequence: 'The target says the position is closed and the book says it is open. Drift, the monthly plan and every weight on the allocation page are computed against a position the policy no longer wants.',
      remedy: 'Portfolio → Exit Holding on each, at the actual fill. If the sale has not happened yet, this row is the reminder that it has not.',
      page: 'portfolio',
    };
    if (cut.length) {
      add({ ...common, status: WARN,
        detail: `${cut.length} position(s) at 0% target still holding: ${cut.slice(0, 6).join(', ')}${cut.length > 6 ? ` +${cut.length - 6} more` : ''}` });
    } else {
      add({ ...common, status: OK, detail: 'every 0% target is actually at zero' });
    }
  }

  // ---- THE OPEN MONTH ----------------------------------------------------------------
  // Two checks for the same failure, which is the one nobody was watching for: a month that
  // is missing something rather than holding something wrong. Every other check on this page
  // grades a value that EXISTS. Nothing graded an absence.
  //
  // On 2026-08-04 the owner asked where two card payments had gone. They had never been
  // written — the expense modal held new rows in memory and only persisted on confirm or on
  // an explicit Save (fixed in cashflow.js the same day). But the fix only stops the NEXT
  // one. What made this cost a morning is that August's cashflow sat at 4 items while April
  // through July each had 7, and no surface in the app said so. Pgs, Bonus and Maximum had
  // been paid every single month for four months and were simply not there.
  //
  // The month's plan row had the same shape: 2026-08 had none, so the Monthly Plan hero fell
  // back to the newest unreconciled month and printed MARCH 2026 under the words THIS MONTH
  // PLAN. Both are absences. Both are now graded. (audit 2026-08-04)
  {
    const monthKey = new Date(now).toISOString().slice(0, 7);
    const cf = (s.cashflow && typeof s.cashflow === 'object') ? s.cashflow : {};
    const monthsBefore = Object.keys(cf).filter(k => /^\d{4}-\d{2}$/.test(k) && k < monthKey).sort();
    const sample = monthsBefore.slice(-3);
    const nameOf = it => String((it && it.name) || '').trim().toLowerCase();
    const namesIn = k => new Set(((cf[k] && cf[k].items) || []).map(nameOf).filter(Boolean));
    const cur = cf[monthKey];

    // "Recurring" = present in at least 2 of the last 3 months. One appearance is a one-off;
    // demanding all three would go quiet the first time a bill is skipped.
    const tally = {};
    sample.forEach(k => namesIn(k).forEach(n => { tally[n] = (tally[n] || 0) + 1; }));
    const recurring = Object.keys(tally).filter(n => tally[n] >= 2);
    const present = cur ? namesIn(monthKey) : new Set();

    // ── A DEFINITION THAT RETIRED IS NOT A BILL THAT WENT MISSING ───────────────────────────
    //
    // `recurring` is built from the item NAME history and nothing else, so a bill that has ENDED
    // keeps being expected until it falls out of the three-month lookback. Measured 2026-10-02:
    // "missing On Credit 2 — paid in 2 of the last 3 months", on a loan whose own definition says
    // `active: false` and `loanRemain: 0`. The owner's answer was one line: it closed last month.
    //
    // `cfGenItems` already knows: it generates a month's rows through `defActiveForMonth`, which is
    // why October has no On Credit 2 row to begin with. The generator reads the definitions and
    // this check did not, so the app withheld the row and then warned that the row was withheld.
    //
    // SCOPE IS THE DATE-FREE HALF OF `defActiveForMonth`, deliberately. That function retires a
    // definition on three signals: `active === false`, a Loan with `loanRemain <= 0`, and the
    // date-bounded branches (`loanRemain` against `cfLoanAnchor()`, or `loanStart + loanTotal`).
    // The first two mean "retired, full stop" and are reproducible here; the last needs the loan
    // anchor, and this module imports nothing. That omission is cheap: a loan that runs out
    // mid-window stops appearing and the three-month lookback drops it by itself within two
    // months. Permanent noise only comes from a definition retired outright, which is this.
    //
    // A name with no definition behind it (a one-off line, `defId: null`) is untouched: absence of
    // a definition is not a retired definition, which is the mistake `cashflow.noPaidBy` made.
    const _defsByName = {};
    (Array.isArray(s.expenseDefs) ? s.expenseDefs : []).forEach(d => {
      if (!d) return;
      const n = String(d.name || '').trim().toLowerCase();
      if (!n) return;
      (_defsByName[n] || (_defsByName[n] = [])).push(d);
    });
    const _retired = d => !!d && (d.active === false
      || (d.type === 'Loan' && d.loanRemain != null && Number(d.loanRemain) <= 0));
    const _allRetired = n => {
      const ds = _defsByName[n];
      return !!ds && ds.length > 0 && ds.every(_retired);
    };
    const missingRecurring = recurring.filter(n => !present.has(n) && !_allRetired(n));
    // Report the name as the owner typed it, not lower-cased.
    const pretty = missingRecurring.map(n => {
      for (const k of sample.slice().reverse()) {
        const hit = ((cf[k] && cf[k].items) || []).find(it => nameOf(it) === n);
        if (hit && hit.name) return hit.name;
      }
      return n;
    });

    const cfCommon = {
      label: 'Recurring expenses this month',
      consequence: 'A bill that is never entered is never projected. Disposable income, surplus and the reserve target all read high by whatever is missing, and the month closes as if it had been paid.',
      remedy: 'Open Cash Flow → this month and add the missing item(s), or confirm they genuinely do not apply.',
      page: 'cashflow',
    };
    if (sample.length < 2) {
      add({ ...cfCommon, id: 'cashflow.recurring', status: UNKNOWN,
        detail: `only ${sample.length} earlier month(s) recorded — not enough history to know what recurs` });
    } else if (!cur) {
      add({ ...cfCommon, id: 'cashflow.recurring', status: WARN,
        detail: `no cash-flow month for ${monthKey}; ${recurring.length} item(s) recur in ${sample.length > 1 ? sample.join(', ') : sample[0]}` });
    } else if (missingRecurring.length) {
      add({ ...cfCommon, id: 'cashflow.recurring', status: WARN,
        detail: `${monthKey} has ${((cur.items) || []).length} item(s); missing ${pretty.join(', ')} — paid in ${Math.max(...missingRecurring.map(n => tally[n]))} of the last ${sample.length} months` });
    } else {
      add({ ...cfCommon, id: 'cashflow.recurring', status: OK,
        detail: `${monthKey} carries all ${recurring.length} item(s) that recur in the last ${sample.length} months` });
    }

    // The monthly plan row for this calendar month.
    const rates = (s.monthlyRates && typeof s.monthlyRates === 'object') ? s.monthlyRates : {};
    const planKeys = Object.keys(rates).filter(k => /^\d{4}-\d{2}$/.test(k)).sort();
    const staleOpen = planKeys.filter(k => k < monthKey && rates[k]
      && rates[k].status !== 'reconciled' && rates[k].status !== 'legacy');
    const planCommon = {
      label: 'Plan row for this month',
      consequence: 'With no row for this month the deployment card has nothing to show for it, and the monthly contribution goes unrecorded against the base.',
      remedy: 'Monthly → Start month, during the 28–31 or 1–4 buy window.',
      page: 'monthly',
    };
    const staleNote = staleOpen.length ? ` · ${staleOpen.length} earlier month(s) still open: ${staleOpen.join(', ')}` : '';
    if (!rates[monthKey]) {
      add({ ...planCommon, id: 'plan.currentMonth', status: WARN,
        detail: `no plan row for ${monthKey}${staleNote}` });
    } else {
      const r = rates[monthKey];
      const filled = r.entries ? Object.keys(r.entries).length : 0;
      add({ ...planCommon, id: 'plan.currentMonth',
        status: staleOpen.length ? WARN : OK,
        detail: `${monthKey} ${r.status === 'reconciled' ? 'reconciled' : filled + ' entr' + (filled === 1 ? 'y' : 'ies') + ' recorded'}${staleNote}` });
    }

    // ── A month entry that contradicts the lots underneath it ────────────────
    //
    // The Monthly Plan and `+ Lot` write the same fact through two doors and, until
    // 2026-09-03, neither could see the other: `saveAddLot` appends a lot, debits cash and
    // books a `portfolio_buy`; `saveMo` writes a month entry and blends the quantity into
    // `h.qty`. A row that went through both carries the purchase TWICE in the position.
    //
    // `saveMo` now refuses such a row and the screen offers no button for it, but a merge from
    // an older build can still put a plain entry beside a lot. There is no benign reading of
    // that state, so it grades CRITICAL and names the rows. Prevention that cannot be observed
    // is a hope; this is the observation. (audit 2026-09-03)
    //
    // This module imports NOTHING by contract, so the comparison is INJECTED. Without it the
    // row reports UNKNOWN, which is the honest answer and is not an OK.
    {
      const echoFn = (opts && typeof opts.lotEchoes === 'function') ? opts.lotEchoes : null;
      const hs = Array.isArray(s.holdings) ? s.holdings : [];
      // A book with no lots anywhere cannot hold this contradiction, and saying UNKNOWN about
      // an impossibility is noise, not honesty. UNKNOWN is reserved for the case where the
      // question is real and the caller did not supply the means to answer it — the same
      // shape `portfolio.accumulateTrigger` uses.
      const anyLots = hs.some(x => x && Array.isArray(x.lots) && x.lots.some(l => l && l.type !== 'drip'));
      const found = [];
      if (echoFn) {
        Object.keys(rates).filter(k => /^\d{4}-\d{2}$/.test(k)).forEach(k => {
          (echoFn(hs, k, rates[k] && rates[k].entries) || []).forEach(x => found.push(x));
        });
      }
      add({
        id: 'plan.lotEcho',
        label: 'No month entry doubles a lot',
        status: !anyLots ? OK : !echoFn ? UNKNOWN : (found.length ? CRITICAL : OK),
        detail: !anyLots ? 'no lot-bearing rows'
          : !echoFn ? 'lot comparison not injected by this caller'
          : found.length
            ? `${found.length} row(s) counted twice: ${found.slice(0, 4).map(x => `${x.ticker} ${x.month} (entry ${x.entryQty} vs lots ${x.lotQty})`).join(' · ')}`
            : `${Object.keys(rates).length} month(s) clean`,
        consequence: found.length
          ? 'The position holds the purchase twice: once from the lot, once from the month entry. '
            + 'Quantity, average cost and net worth are all overstated by the duplicate, and every '
            + 'drift band and deployment plan reads off the inflated figure.'
          : '',
        remedy: 'Monthly → open the month → the affected row, and delete the typed entry. The lot is the record.',
        page: 'monthly',
      });
    }

    // ── Portfolio commitment vs the plan that predicts it ────────────────────
    // The one hand-typed number on the Money page that a formula CAN predict, and nothing was
    // comparing. `portfolioComm` is auto-filled once from base x multiplier x FX and is then
    // free to be edited to anything; no code recomputes it and no check graded it.
    //
    // On 2026-08-08 a rebalance month's redeployed proceeds were typed into it — ₺113,832
    // became ₺168,405, then ₺227,668 — and because `commitment = portfolioComm − transferred`
    // treats the whole figure as a claim on income, the same ₺113,836 was subtracted twice.
    // Free TRY read ₺134,439 against a true ₺248,275 for five days, on a number that reaches
    // the Money hero, the cash projection, TRY IDLE SHARE, the liability payment preview and
    // the LLM prompt. The owner found it, not this file. (MOVE-004 / CONTRACT-004)
    //
    // The band is 10%. Real drift is FX between the day the field was auto-filled (S.fxRate)
    // and the day the month reconciled (monthlyRates[k].usdtry): measured across the owner's
    // five recorded months it is 0.09%–0.50%. The 2026-08 plug was +99.5%. Anything the
    // multiplier is meant to express — a half month, a skipped month — belongs in `mul`, and
    // `mul` is inside the prediction, so using it correctly keeps this green.
    const base = Number(s.base);
    const pcCommon = {
      label: 'Portfolio commitment vs plan',
      consequence: 'portfolioComm is subtracted from free TRY as a claim on this month\'s income. Capital recycled from a sale was already debited by the ledger when the buy was booked, so putting it here subtracts the same money a second time — on the Money hero, the cash projection, TRY IDLE SHARE, the liability payment preview and the AI prompt.',
      remedy: 'Cash Flow → open the month. New money from income goes in Portfolio Commitment; capital recycled from sales goes in Redeployed, which is subtracted nowhere. A month you deliberately contributed more or less into is said with the multiplier on Monthly — the multiplier is inside this prediction, so using it keeps the check green.',
      page: 'cashflow',
    };
    if (!isFinite(base) || base <= 0) {
      add({ ...pcCommon, id: 'cashflow.commitmentPlan', status: UNKNOWN,
        detail: 'no monthly base recorded — nothing to predict the commitment from' });
    } else {
      const off = [];
      let graded = 0;
      Object.keys(cf).filter(k => /^\d{4}-\d{2}$/.test(k)).sort().forEach(k => {
        const mo = cf[k];
        if (!mo || typeof mo !== 'object') return;
        const row = rates[k] || null;
        // The month's own reconciliation rate is the honest anchor. Only the CURRENT month may
        // fall back to the live rate: for any earlier month that is a rate from the future.
        const fx = Number(row && row.usdtry) || (k === monthKey ? Number(s.fxRate) : NaN);
        if (!isFinite(fx) || fx <= 0) return;
        const mul = Number(row && row.mul);
        const plan = base * (isFinite(mul) && mul > 0 ? mul : 1) * fx;
        if (!(plan > 0)) return;
        graded++;
        const got = Number(mo.portfolioComm) || 0;
        if (Math.abs(got - plan) > plan * 0.10) {
          off.push({ k, got, plan, gap: got - plan, redeployed: Number(mo.redeployed) || 0 });
        }
      });
      if (!graded) {
        add({ ...pcCommon, id: 'cashflow.commitmentPlan', status: UNKNOWN,
          detail: 'no month has both a plan row and an FX rate to predict from' });
      } else if (off.length) {
        add({ ...pcCommon, id: 'cashflow.commitmentPlan', status: WARN,
          detail: off.map(o => `${o.k} ₺${_n(o.got)} vs plan ₺${_n(o.plan)} (${o.gap > 0 ? '+' : '−'}₺${_n(Math.abs(o.gap))})${o.redeployed ? ` · ₺${_n(o.redeployed)} already recorded as redeployed` : ''}`).join(' · ') });
      } else {
        add({ ...pcCommon, id: 'cashflow.commitmentPlan', status: OK,
          detail: `${graded} month(s) within 10% of base x multiplier x FX` });
      }
    }
  }

  const counts = checks.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; },
    { ok: 0, warn: 0, critical: 0, unknown: 0 });
  const status = counts.critical ? CRITICAL : counts.warn ? WARN : counts.unknown ? UNKNOWN : OK;

  return { generatedAt: now, status, counts, checks };
}

/** Checks that are not OK, worst first. This is what a UI should surface. */
export function integrityIssues(S, now = Date.now(), opts = null) {
  const rank = { critical: 0, unknown: 1, warn: 2, ok: 3 };
  return integrityReport(S, now, opts).checks
    .filter(c => c.status !== OK)
    .sort((a, b) => rank[a.status] - rank[b.status]);
}
